import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Upload,
  Film,
  X,
  Loader2,
  Download,
  Play,
  Trash2,
  Sparkles,
  Subtitles,
  Mic,
  Video as VideoIcon,
  Images as ImagesIcon,
  FileAudio,
  UserRound,
  Globe,
  Eye,
  EyeOff,
  Link as LinkIcon,
  Copy,
  Check,
  Share2,
  FileText,
  ThumbsUp,
  ThumbsDown,
  Heart,
  RotateCcw,
  Facebook,
  Volume2,
  Pencil,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Youtube,
} from "lucide-react";
import ShareButtons from "@/ShareButtons";
import { mediaUrl } from "@/mediaUrl";
import YouTubeKitModal from "@/YouTubeKitModal";
import StoryBodyEditor from "@/StoryBodyEditor";
import StudioWizard from "@/StudioWizard";
import SubmissionsPanel from "@/SubmissionsPanel";
import DonationLinksPanel from "@/DonationLinksPanel";
import BatchAutoWritePanel from "@/BatchAutoWritePanel";
import BatchPretranslatePanel from "@/BatchPretranslatePanel";
import PromoReelPanel from "@/PromoReelPanel";
import TaxonomyEditor from "@/TaxonomyEditor";
import TaxonomyBatchPanel from "@/TaxonomyBatchPanel";
import FixSeriesOrderPanel from "@/FixSeriesOrderPanel";
import HealLanguagesButton from "@/HealLanguagesButton";
import ThumbHealButton from "@/ThumbHealButton";
import MusicMixButton from "@/MusicMixButton";
import TilloPanel from "@/TilloPanel";
import VoicesPanel from "@/VoicesPanel";
import ThumbnailUploadButton from "@/ThumbnailUploadButton";
import AddTailsButton from "@/AddTailsButton";
import RefreshEndcardButton from "@/RefreshEndcardButton";
import LanguagesButton from "@/LanguagesButton";
import LanguageProgressStrip from "@/LanguageProgressStrip";
import SeriesLinkButton from "@/SeriesLinkButton";
import StoryLabelBadges from "@/StoryLabelBadges";
import StudioLibraryFilters from "@/StudioLibraryFilters";
import ScriptToAudioPanel from "@/ScriptToAudioPanel";
import MultiPartBuilderPanel from "@/MultiPartBuilderPanel";
import RecoverStrandedButton from "@/RecoverStrandedButton";
import BackfillDurationsButton from "@/BackfillDurationsButton";
import TranscriptBackfillButton from "@/TranscriptBackfillButton";
import TractionPanel from "@/TractionPanel";
import ShortLinksPanel from "@/ShortLinksPanel";
import YouTubeImportButton from "@/YouTubeImportButton";
import LanguageRefreshButton from "@/LanguageRefreshButton";
import TailSubsButton from "@/TailSubsButton";
import FacebookShareModal from "@/FacebookShareModal";
import FBPreviewModal from "@/FBPreviewModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Retry helpers for transient network hiccups (WiFi dropouts, CDN 502s,
// brief connection resets). All calls that survive a "Network Error"
// bubble go through these instead of raw axios. Non-retryable 4xx
// errors (like 413 payload too big) surface immediately.
const _RETRY_DELAYS_MS = [0, 1_500, 4_000, 10_000]; // 4 attempts, ~15 s budget

const _isRetryable = (err) => {
  const status = err?.response?.status;
  if (status == null) return true;                // network error, no HTTP response
  if (status === 408 || status === 429) return true;
  if (status >= 500) return true;                 // 5xx (incl. Cloudflare 520/502/504)
  return false;                                    // 4xx client errors — don't retry
};

const postWithRetry = async (url, body, options = {}) => {
  let lastErr = null;
  for (let a = 0; a < _RETRY_DELAYS_MS.length; a++) {
    if (_RETRY_DELAYS_MS[a] > 0) {
      await new Promise((r) => setTimeout(r, _RETRY_DELAYS_MS[a]));
    }
    try {
      const { data } = await axios.post(url, body, options);
      return data;
    } catch (err) {
      lastErr = err;
      if (!_isRetryable(err)) throw err;
      // eslint-disable-next-line no-console
      console.warn(`POST ${url} attempt ${a + 1} failed:`, err?.message || err);
    }
  }
  throw lastErr;
};

const getWithRetry = async (url, options = {}) => {
  let lastErr = null;
  for (let a = 0; a < _RETRY_DELAYS_MS.length; a++) {
    if (_RETRY_DELAYS_MS[a] > 0) {
      await new Promise((r) => setTimeout(r, _RETRY_DELAYS_MS[a]));
    }
    try {
      const { data } = await axios.get(url, options);
      return data;
    } catch (err) {
      lastErr = err;
      if (!_isRetryable(err)) throw err;
      // eslint-disable-next-line no-console
      console.warn(`GET ${url} attempt ${a + 1} failed:`, err?.message || err);
    }
  }
  throw lastErr;
};

const LANGUAGE_FLAG = {
  en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸", it: "🇮🇹", el: "🇬🇷",
  tr: "🇹🇷", ar: "🇸🇦", nl: "🇳🇱", ru: "🇷🇺",
};

function App() {
  // shared
  const [languages, setLanguages] = useState([]);
  const [voicesByLang, setVoicesByLang] = useState({});
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  // Primary / source language. Used to be hardcoded "en" — but creators
  // upload non-English-narrated videos all the time, and the wrong source
  // language broke the entire dub pipeline (the German→English bug).
  // Now a real state value with a dropdown picker that auto-flips when
  // Whisper detects the spoken language of the upload.
  const [language, setLanguage] = useState("en");
  // Whisper-detect result for the most recent upload: {language, raw,
  // confirmed?} so we can show a "Detected: 🇩🇪 German — looks right?"
  // chip the creator can dismiss/correct.
  const [detectedLang, setDetectedLang] = useState(null);
  const voice = "en-GB-SoniaNeural";
  // "female" or "male" — used for the primary render AND auto-mapped to each
  // of the other 8 languages so the whole story sounds consistent.
  const [voiceGender, setVoiceGender] = useState("female");
  const [subtitles, setSubtitles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [library, setLibrary] = useState([]);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [reorderRows, setReorderRows] = useState([]);
  // Story whose share-card preview is currently open in the modal. `null`
  // when closed. We store the whole video doc so the modal can show the
  // live URL + a copy-link button alongside the rendered card.
  const [sharePreview, setSharePreview] = useState(null);
  // Story to auto-post to Facebook Page. Admin-only — uses the stored
  // Page Access Token in backend env. See FacebookShareModal.jsx +
  // /api/admin/share/facebook/{id}.
  const [fbShare, setFbShare] = useState(null);
  // Story whose live FB share-card preview is open. Shows the EXACT
  // og:image FB will scrape + a "Refresh FB cache" button.
  const [fbPreview, setFbPreview] = useState(null);
  // Story whose YouTube/Spotify upload kit modal is open.
  const [ytKit, setYtKit] = useState(null);
  // Story whose long-form article editor is open. Same pattern.
  const [bodyEditor, setBodyEditor] = useState(null);
  // Story whose taxonomy editor (main category / country / tags / labels)
  // is open. `null` when closed.
  const [taxEditor, setTaxEditor] = useState(null);
  // Client-side filtered library (search + category + toggles). Mirrors
  // `library` when no filters are active. Reset whenever `library`
  // reloads so a fresh render doesn't keep stale filtered results.
  const [filteredLibrary, setFilteredLibrary] = useState([]);
  // Toggle for the guided-mode wizard preview.
  const [wizardOpen, setWizardOpen] = useState(false);
  // When the studio owner clicks "Use this →" on a public submission, we
  // park the per-story metadata (donation_link, category, source URL hints
  // and the submission id itself) here so the next successful render can
  // PATCH them onto the new video doc and mark the submission as featured.
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(() => {
    // One-shot deep link into a studio mode (e.g. "Re-edit parts"
    // reloads the page and must land back on the Multi-part tab).
    const m = window.localStorage.getItem("storylo_studio_mode");
    if (m) {
      window.localStorage.removeItem("storylo_studio_mode");
      return m;
    }
    return "dub";
  }); // "dub" | "publish_as_is" | "script_to_audio" | "multi_part"
  // ── Dual-picker preview model (mirrors PublicViewer) ──────────────
  // `previewAudioLang`: which language's AUDIO plays in the preview.
  //   Default = the story's source language (the speaker's original
  //   voice — the most authentic playback, per user direction Q1=a).
  // `previewSubLang`: which language's SUBTITLES overlay (or "off").
  //   Default = the per-story `default_subtitle_lang` override if set,
  //   else "off" (creator usually wants to verify source plays clean).
  const [previewAudioLang, setPreviewAudioLang] = useState(null);
  const [previewSubLang, setPreviewSubLang] = useState(null);
  // Captions toggle for the VTT track in Studio preview.
  const [previewCcOn, setPreviewCcOn] = useState(true);
  const previewVideoRef = useRef(null);

  // dub mode
  const [videoFile, setVideoFile] = useState(null);
  // When transcription fails, remember WHY so we can render the right
  // recovery UI. Null = no error; "upload_missing" = actionable card;
  // "generic" = generic banner.
  const [transcribeErrorKind, setTranscribeErrorKind] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  // When the user pastes a public URL (YouTube, TikTok, IG, X, direct mp4…),
  // we fetch it server-side via yt-dlp and stash the resulting upload_id here.
  // Treated identically to a locally-uploaded file from then on.
  const [videoUrl, setVideoUrl] = useState("");
  const [videoFetching, setVideoFetching] = useState(false);
  const [videoFetched, setVideoFetched] = useState(null); // { upload_id, filename, size }
  // When true, the narration is extracted from the animation video itself —
  // no separate .wav upload is needed. This is the common case for Aysin's
  // animated clips that already have the English voiceover baked in.
  const [useVideoVoiceover, setUseVideoVoiceover] = useState(true);
  const [scriptCopied, setScriptCopied] = useState(false);
  // Append a 2.5s Storylo end-card to the rendered video. ON by default — gives
  // each downloaded MP4 a tasteful brand sign-off when posted to TikTok / Reels.
  const [brandedEndcard, setBrandedEndcard] = useState(true);
  // Publish-as-is only: when true, after the render finishes we run Whisper
  // in the background to populate `script_text` so per-language translation
  // chips work on the public viewer (without ever re-dubbing the audio).
  const [transcribeForSeo, setTranscribeForSeo] = useState(true);
  const [tilloEpisode, setTilloEpisode] = useState(false);
  const [tilloSongTitle, setTilloSongTitle] = useState("");
  const [tilloLyrics, setTilloLyrics] = useState("");
  // Tails are an ordered list. Each:
  //   { id, file?, preview?, url, fetching, fetched?,
  //     transcribing, transcribeMessage, transcript, transcriptInserted }
  const newTailSlot = () => ({
    id: crypto.randomUUID(), file: null, preview: null, url: "",
    fetching: false, fetched: null,
    transcribing: false, transcribeMessage: "", transcript: "",
    transcriptInserted: false,
  });
  const [tails, setTails] = useState([newTailSlot()]);
  const [audioFile, setAudioFile] = useState(null);
  const [transcribing, setTranscribing] = useState(false);

  // slideshow mode
  const [images, setImages] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const imgInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const pollRef = useRef(null);
  const tailInputRefs = useRef({});

  // init
  useEffect(() => {
    (async () => {
      try {
        const [l, v, lib] = await Promise.all([
          axios.get(`${API}/languages`),
          axios.get(`${API}/voices`),
          axios.get(`${API}/videos`),
        ]);
        setLanguages(l.data);
        setVoicesByLang(v.data);
        setLibrary(lib.data);
      } catch (e) { console.error(e); }
    })();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  // iOS Safari pauses setInterval when the tab is backgrounded / phone locks.
  // Re-poll immediately on visibility or focus to catch up.
  useEffect(() => {
    const resume = () => {
      if (document.visibilityState === "visible" && activeJobIdRef.current) {
        pollOnce(activeJobIdRef.current);
      }
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- slideshow image handling ---------- */
  const addImages = useCallback((files) => {
    const incoming = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .map((f) => ({ file: f, preview: URL.createObjectURL(f), id: `${f.name}-${f.size}-${Math.random()}` }));
    setImages((prev) => [...prev, ...incoming]);
  }, []);

  const onImageDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) addImages(e.dataTransfer.files);
  };

  const removeImage = (id) => {
    setImages((prev) => {
      const t = prev.find((p) => p.id === id);
      if (t) URL.revokeObjectURL(t.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const moveImage = (idx, dir) => {
    setImages((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  /* ---------- dub file handling ---------- */
  const onVideoSelect = (f) => {
    if (!f) return;
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(f);
    setVideoPreview(URL.createObjectURL(f));
    setVideoFetched(null);
    setVideoUrl("");
    // A fresh source clears any prior transcribe-recovery banner.
    setTranscribeErrorKind(null);
    setError("");
    // Only dub mode needs an upfront script — Publish-as-is keeps the
    // original audio and transcribes in the BACKGROUND after publish, so
    // auto-transcribing here just locked the Publish button for nothing.
    if (useVideoVoiceover && mode === "dub") transcribeFromVideo(f);
  };

  // If a source was picked while in Publish-as-is (no transcription
  // needed) and the creator then switches to Dub mode, fetch the script
  // at that moment instead.
  useEffect(() => {
    if (mode === "dub" && useVideoVoiceover && !text.trim() && !transcribing) {
      if (videoFile) transcribeFromVideo(videoFile);
      else if (videoFetched) transcribeFromVideo(null, videoFetched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const clearSourceVideo = () => {
    setVideoFile(null);
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoPreview(null);
    setVideoFetched(null);
    setVideoUrl("");
    setDetectedLang(null);
  };

  /** Fetch the source video from a pasted URL via yt-dlp. Mirrors the tail-clip
   *  pattern so the user can author a dub from a YouTube / IG / TikTok / X /
   *  direct-mp4 link without manual download. Auto-runs Whisper afterwards
   *  when the "use voiceover from this video" toggle is on. */
  const fetchSourceVideoUrl = async () => {
    const url = (videoUrl || "").trim();
    if (!url) return;
    setVideoFetching(true);
    setError("");
    try {
      const form = new FormData();
      form.append("url", url);
      const { data } = await axios.post(`${API}/fetch-url`, form, {
        timeout: 10 * 60 * 1000,
      });
      // Replace any local file with the fetched server-side upload.
      if (videoPreview) URL.revokeObjectURL(videoPreview);
      setVideoFile(null);
      setVideoPreview(null);
      setVideoFetched(data);
      if (useVideoVoiceover && mode === "dub") {
        // Reuse the upload_id directly — no re-upload needed.
        transcribeFromVideo(null, { upload_id: data.upload_id, filename: data.filename });
      }
    } catch (e) {
      console.error(e);
      const parts = [];
      if (e.response?.status) parts.push(`HTTP ${e.response.status}`);
      if (e.response?.data?.detail) parts.push(e.response.data.detail);
      else if (e.message) parts.push(e.message);
      setError(`Could not fetch that link — ${parts.join(" · ")}`);
    } finally {
      setVideoFetching(false);
    }
  };

  /** Transcribe the source video. Either pass a local `File` (which gets
   *  chunk-uploaded first), or an `{upload_id, filename}` of an already-
   *  uploaded / fetched server-side video to skip the upload entirely. */
  const transcribeFromVideo = async (f, existing = null) => {
    if (!f && !existing) return;
    setTranscribing(true);
    setError("");
    setUploadProgress(0);
    try {
      let upload_id, filename;
      if (existing) {
        upload_id = existing.upload_id;
        filename = existing.filename || "video.mp4";
      } else {
        const up = await uploadFileInChunks(f, setUploadProgress);
        upload_id = up.upload_id;
        filename = up.filename;
      }
      const startForm = new FormData();
      startForm.append("upload_id", upload_id);
      startForm.append("filename", filename);
      // /start is a fast Mongo lookup + insert (see round-4 fix in
      // PRD.md), but a transient CDN blip mid-response should still be
      // survivable rather than abort the whole upload flow the user
      // just waited on.
      const started = await postWithRetry(
        `${API}/transcribe-from-video-upload/start`,
        startForm,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60_000 },
      );
      const jobId = started.job_id;

      // Poll until done. Long videos can take several minutes.
      const deadline = Date.now() + 30 * 60 * 1000; // 30 min hard cap
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) throw new Error("Transcription timed out after 30 minutes.");
        await new Promise((r) => setTimeout(r, 3000));
        // Poll must tolerate a transient network hiccup — a single
        // failed GET should not fail the whole transcription of a
        // 10-minute upload the user already paid for with an upload.
        // getWithRetry retries silently up to 3× with backoff and
        // only surfaces the error after all retries fail.
        let status;
        try {
          status = await getWithRetry(
            `${API}/transcribe-from-video-upload/${jobId}`,
            { timeout: 30_000 },
          );
        } catch (pollErr) {
          // Skip this tick — the loop will retry on the next
          // 3-second heartbeat rather than crashing the whole flow.
          // eslint-disable-next-line no-console
          console.warn("Transcribe poll transient failure — retrying:", pollErr?.message || pollErr);
          continue;
        }
        if (status.status === "done") {
          if (status.text) setText(status.text);
          break;
        }
        if (status.status === "failed") {
          throw new Error(status.error || "Transcription failed.");
        }
      }
    } catch (e) {
      console.error("Video-voiceover transcription error:", e);
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || "unknown error";
      const isUploadMissing =
        status === 404 &&
        /upload not found/i.test(String(detail));
      if (isUploadMissing) {
        // Special-case: the chunked upload isn't reachable any more
        // (typically because a previous publish/dub already cleaned it
        // up from Object Store, or the local .bin was purged). Render
        // an actionable recovery card instead of a wall of text — the
        // user can pick "Re-upload", "Use separate audio" or
        // "Type the script" with a single click.
        setTranscribeErrorKind("upload_missing");
        setError("The uploaded video isn't reachable any more — that usually means a previous publish or refresh cleaned it up. Pick one of the recovery options below.");
      } else {
        const parts = [];
        if (status) parts.push(`HTTP ${status}`);
        parts.push(detail);
        setTranscribeErrorKind("generic");
        setError(`Couldn't transcribe the video's voiceover — ${parts.join(" · ")}. You can untick "Use voiceover from video" and upload a separate audio file, or type the script by hand.`);
      }
    } finally {
      setTranscribing(false);
      setUploadProgress(0);
    }
  };

  /* ---------- tail videos (multiple) ---------- */
  const updateTail = (id, patch) => {
    setTails((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const addTail = () => {
    setTails((prev) => [...prev, newTailSlot()]);
  };

  const removeTail = (id) => {
    setTails((prev) => {
      const target = prev.find((t) => t.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      const next = prev.filter((t) => t.id !== id);
      // keep at least one empty slot
      return next.length ? next : [newTailSlot()];
    });
  };

  const onTailFileSelect = (id, f) => {
    if (!f) return;
    const t = tails.find((x) => x.id === id);
    if (t?.preview) URL.revokeObjectURL(t.preview);
    updateTail(id, {
      file: f, preview: URL.createObjectURL(f), fetched: null,
      transcript: "", transcriptInserted: false, transcribeMessage: "",
    });
  };

  /** Bulk-add many files at once. The first file replaces the empty slot the
   *  user clicked into; each remaining file becomes a fresh slot below it. */
  const onTailFilesBulk = (id, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    setTails((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1) return prev;
      const next = prev.slice();
      // First file fills the clicked slot
      const first = list[0];
      const slot = next[idx];
      if (slot.preview) URL.revokeObjectURL(slot.preview);
      next[idx] = {
        ...slot, file: first, preview: URL.createObjectURL(first), fetched: null,
        transcript: "", transcriptInserted: false, transcribeMessage: "",
      };
      // Remaining files become new slots inserted right after
      const newSlots = list.slice(1).map((f) => ({
        ...newTailSlot(),
        file: f,
        preview: URL.createObjectURL(f),
      }));
      next.splice(idx + 1, 0, ...newSlots);
      return next;
    });
  };

  const fetchTailUrl = async (id) => {
    const t = tails.find((x) => x.id === id);
    const url = (t?.url || "").trim();
    if (!url) return;
    updateTail(id, { fetching: true });
    setError("");
    try {
      const form = new FormData();
      form.append("url", url);
      const { data } = await axios.post(`${API}/fetch-url`, form, {
        timeout: 10 * 60 * 1000,
      });
      updateTail(id, {
        fetching: false, fetched: data, file: null, preview: null,
        transcript: "", transcriptInserted: false, transcribeMessage: "",
      });
    } catch (e) {
      console.error(e);
      const parts = [];
      if (e.response?.status) parts.push(`HTTP ${e.response.status}`);
      if (e.response?.data?.detail) parts.push(e.response.data.detail);
      else if (e.message) parts.push(e.message);
      setError(`Could not fetch that link — ${parts.join(" · ")}`);
      updateTail(id, { fetching: false });
    }
  };

  const clearTail = (id) => {
    const t = tails.find((x) => x.id === id);
    if (t?.preview) URL.revokeObjectURL(t.preview);
    updateTail(id, {
      file: null, preview: null, url: "", fetched: null,
      transcript: "", transcriptInserted: false, transcribeMessage: "",
    });
  };

  /** Fetch (if URL) → extract audio → transcribe with Whisper, all server-side.
   *  The returned text is stored on the tail-clip slot so the user can preview
   *  it and one-click insert (or append) it into the main narration script. */
  const transcribeTail = async (id) => {
    const t = tails.find((x) => x.id === id);
    if (!t) return;
    const url = (t.url || "").trim();
    const fetchedId = t.fetched?.upload_id;
    const fetchedName = t.fetched?.filename;

    // For locally-uploaded tail files we need to chunk-upload them first so
    // the backend has a known upload_id to extract audio from.
    let upload_id = fetchedId;
    let filename = fetchedName || "video.mp4";
    let useUrl = "";

    if (!fetchedId && t.file) {
      try {
        updateTail(id, {
          transcribing: true, transcribeMessage: "Uploading tail clip…",
          transcript: "", transcriptInserted: false,
        });
        const up = await uploadFileInChunks(t.file, () => {});
        upload_id = up.upload_id;
        filename = up.filename;
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Upload failed";
        updateTail(id, { transcribing: false, transcribeMessage: "" });
        setError(`Couldn't upload tail clip — ${detail}`);
        return;
      }
    } else if (!fetchedId && url) {
      useUrl = url;
    } else if (!fetchedId && !t.file) {
      setError("Add a tail clip file or paste a URL first.");
      return;
    }

    updateTail(id, {
      transcribing: true,
      transcribeMessage: useUrl ? "Downloading video…" : "Extracting audio…",
      transcript: "", transcriptInserted: false,
    });
    setError("");

    try {
      const form = new FormData();
      if (useUrl) form.append("url", useUrl);
      if (upload_id) {
        form.append("upload_id", upload_id);
        form.append("filename", filename);
      }
      const { data: started } = await axios.post(
        `${API}/tail-clips/transcribe-from-url/start`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60_000 },
      );
      const jobId = started.job_id;
      const deadline = Date.now() + 30 * 60 * 1000; // 30 min hard cap
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) throw new Error("Transcription timed out after 30 minutes.");
        await new Promise((r) => setTimeout(r, 3000));
        const { data: status } = await axios.get(
          `${API}/tail-clips/transcribe-from-url/${jobId}`,
          { timeout: 30_000 },
        );
        if (status.message) {
          updateTail(id, { transcribeMessage: status.message });
        }
        if (status.status === "done") {
          // If we fetched fresh, also remember the resulting upload so the
          // tail clip can be reused at render time without re-downloading.
          const patch = {
            transcribing: false,
            transcribeMessage: "",
            transcript: status.text || "",
            transcriptInserted: false,
          };
          if (useUrl && status.upload_id) {
            patch.fetched = {
              upload_id: status.upload_id,
              filename: status.filename || "fetched.mp4",
              size: status.size || 0,
            };
            patch.url = "";
          }
          updateTail(id, patch);
          break;
        }
        if (status.status === "failed") {
          throw new Error(status.error || "Transcription failed.");
        }
      }
    } catch (e) {
      console.error("Tail transcription error:", e);
      const parts = [];
      if (e.response?.status) parts.push(`HTTP ${e.response.status}`);
      if (e.response?.data?.detail) parts.push(e.response.data.detail);
      else if (e.message) parts.push(e.message);
      setError(`Couldn't transcribe that clip — ${parts.join(" · ") || "unknown error"}.`);
      updateTail(id, { transcribing: false, transcribeMessage: "" });
    }
  };

  const insertTailTranscript = (id, mode = "append") => {
    const t = tails.find((x) => x.id === id);
    const incoming = (t?.transcript || "").trim();
    if (!incoming) return;
    setText((prev) => {
      const cur = (prev || "").trim();
      if (mode === "replace" || !cur) return incoming;
      return `${cur}\n\n${incoming}`;
    });
    updateTail(id, { transcriptInserted: true });
  };

  const hasAnyTail = tails.some((t) => t.file || t.fetched);

  const onAudioSelect = async (f) => {
    if (!f) return;
    setAudioFile(f);
    setTranscribing(true);
    setError("");
    setUploadProgress(0);
    try {
      // Chunked upload so large .wav files don't hit nginx 413
      const { upload_id, filename } = await uploadFileInChunks(f, setUploadProgress);
      // Kick off async transcription and poll for the result — the
      // synchronous variant would time out on Cloudflare (HTTP 520)
      // for anything longer than ~90 s of audio (10-min animations
      // narration = 3-5 min Whisper call).
      const startForm = new FormData();
      startForm.append("upload_id", upload_id);
      startForm.append("filename", filename);
      const startResp = await postWithRetry(
        `${API}/transcribe-from-upload/start`, startForm,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60_000 },
      );
      const jobId = startResp?.job_id;
      if (!jobId) throw new Error("Transcription failed to start.");

      // Poll every 3 s for up to 5 minutes. Frontend timeout matches
      // the upload safety net.
      const deadline = Date.now() + 5 * 60 * 1000;
      let text = "";
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        let status;
        try {
          status = await getWithRetry(
            `${API}/transcribe-from-upload/${jobId}`, { timeout: 30_000 },
          );
        } catch (pollErr) {
          // eslint-disable-next-line no-console
          console.warn("Audio poll transient failure — retrying:", pollErr?.message || pollErr);
          continue;
        }
        if (status.status === "done") {
          text = status.text || "";
          break;
        }
        if (status.status === "failed") {
          throw new Error(status.error || "Transcription failed.");
        }
      }
      if (!text && Date.now() >= deadline) {
        throw new Error(
          "Transcription is taking longer than 5 minutes. You can type " +
          "the script manually and rerun the dub, or reload and retry.",
        );
      }
      if (text) setText(text);
    } catch (e) {
      console.error("Transcription error:", e);
      const parts = [];
      if (e.response?.status) parts.push(`HTTP ${e.response.status}`);
      if (e.response?.data?.detail) parts.push(e.response.data.detail);
      else if (e.code) parts.push(e.code);
      else if (e.message) parts.push(e.message);
      setError(`Transcription failed — ${parts.join(" · ") || "unknown error"}. You can type the script manually.`);
    } finally {
      setTranscribing(false);
      setUploadProgress(0);
    }
  };

  /* ---------- chunked upload for large files ---------- */
  const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB per chunk, safely under any nginx limit
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadFileInChunks = async (file, onProgress) => {
    // Returns { upload_id, filename }
    const upload_id = crypto.randomUUID();
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    for (let i = 0; i < total; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const slice = file.slice(start, end);
      // Per-chunk retry with exponential backoff. A 10-min video upload
      // is 25–125 sequential HTTP POSTs — one transient WiFi glitch,
      // Cloudflare 502/503 on a single chunk, or browser connection reset
      // used to abort the whole flow as "Network Error". Server-side is
      // fully idempotent (Mongo `$addToSet: parts_received`), so retrying
      // the exact same request is safe. Budget: 4 attempts, waiting
      // 1 / 3 / 8 seconds between them (~12 s total per bad chunk).
      const attempts = [0, 1_000, 3_000, 8_000];
      let lastErr = null;
      for (let a = 0; a < attempts.length; a++) {
        if (attempts[a] > 0) {
          await new Promise((r) => setTimeout(r, attempts[a]));
        }
        try {
          const form = new FormData();
          form.append("upload_id", upload_id);
          form.append("index", String(i));
          form.append("total", String(total));
          form.append("filename", file.name);
          form.append("chunk", slice, file.name);
          await axios.post(`${API}/upload/chunk`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 5 * 60 * 1000,
          });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          // Non-retryable client errors (4xx except 408/429): bail
          // immediately so the user isn't stuck waiting on a permanent
          // failure like a 413 payload-too-large.
          const status = err?.response?.status;
          if (status && status >= 400 && status < 500 &&
              status !== 408 && status !== 429) {
            throw err;
          }
          // eslint-disable-next-line no-console
          console.warn(
            `Chunk ${i + 1}/${total} failed (attempt ${a + 1}/${attempts.length}): `,
            err?.message || err,
          );
        }
      }
      if (lastErr) throw lastErr;
      if (onProgress) onProgress(Math.round(((i + 1) / total) * 100));
    }
    // The server assembles the final file in the BACKGROUND (doing it
    // inline used to exceed Cloudflare's ~100 s proxy timeout for long
    // films → HTTP 524 on the final chunk). Poll until assembly is done
    // so follow-up calls (transcribe / publish) always find the file.
    const assemblyDeadline = Date.now() + 20 * 60 * 1000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let st = null;
      try {
        st = (await axios.get(`${API}/upload/chunk/status/${upload_id}`, { timeout: 20_000 })).data;
      } catch (_e) { /* transient — keep polling */ }
      if (st?.done) break;
      if (st?.error) throw new Error(`Upload could not be finalised — ${st.error}`);
      if (Date.now() > assemblyDeadline) {
        throw new Error("Upload finalisation timed out — please try again.");
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    return { upload_id, filename: file.name };
  };

  // Whisper-detect the source language of an already-uploaded chunk. Used
  // to auto-flip the language dropdown after upload so creators can't
  // accidentally mark a German-narrated video as English source. Returns
  // the detected ISO code or null on any failure (non-fatal).
  const detectSourceLang = async (upload_id, filename) => {
    try {
      const fd = new FormData();
      fd.append("upload_id", upload_id);
      fd.append("filename", filename || "video.mp4");
      const { data } = await axios.post(`${API}/detect-language-from-upload`, fd, {
        timeout: 90 * 1000,
      });
      if (data?.supported && data.language) {
        setDetectedLang({
          language: data.language,
          language_name: data.language_name,
          raw: data.raw,
          confirmed: data.language === language,  // matches current pick already
        });
        // Auto-flip the source-language dropdown to the detected value.
        // The creator sees the chip and can still override before submit.
        setLanguage(data.language);
        return data.language;
      }
    } catch (_e) { /* non-fatal */ }
    return null;
  };
  /* ---------- job polling (iOS Safari-safe) ---------- */
  const activeJobIdRef = useRef(null);

  const pollOnce = async (id) => {
    try {
      const { data } = await axios.get(`${API}/videos/${id}`);
      setCurrentJob(data);
      setPreviewAudioLang((prev) => prev || (data.status === "done" ? data.language : null));
      setPreviewSubLang((prev) => prev || (data.default_subtitle_lang ? data.default_subtitle_lang : "off"));
      const langsMap = data.languages || {};
      const stillRendering = Object.values(langsMap).some(
        (l) => l.status === "queued" || l.status === "processing"
      );
      if (data.status === "done" || data.status === "failed") {
        setSubmitting(false);
        // If this render was kicked off from a public submission, transfer
        // its donation_link / category / org / link onto the freshly-created
        // story so the green Donate button + Impact tag light up immediately.
        if (data.status === "done" && pendingSubmission && pendingSubmission.video_id !== id) {
          const ps = pendingSubmission;
          try {
            const impact = {};
            if (ps.donation_link) impact.donation_link = ps.donation_link;
            if (ps.category) impact.category = ps.category;
            if (ps.organization_name) impact.organization_name = ps.organization_name;
            if (Object.keys(impact).length) {
              const upd = await axios.patch(`${API}/videos/${id}/impact`, impact);
              setCurrentJob((j) => ({ ...j, ...(upd.data?.fields || {}) }));
            }
            // Mark the submission as featured server-side so we know it's
            // now a real published story.
            const tok = (typeof window !== "undefined" && window.localStorage.getItem("storylo_admin_token")) || "";
            if (tok && ps.submission_id) {
              await axios.patch(`${API}/admin/submissions/${ps.submission_id}`,
                { status: "featured" },
                { headers: { "X-Admin-Token": tok } });
            }
          } catch (e) {
            console.warn("Couldn't apply pending submission metadata:", e?.response?.data || e.message);
          } finally {
            setPendingSubmission(null);
          }
        }
        const lib = await axios.get(`${API}/videos`);
        setLibrary(lib.data);
        if (!stillRendering) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          activeJobIdRef.current = null;
        }
      }
    } catch (e) { console.error("poll err", e); }
  };

  const pollJob = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    activeJobIdRef.current = id;
    pollOnce(id); // fire immediately
    pollRef.current = setInterval(() => pollOnce(id), 2500);
  };

  /* ---------- public submission → studio ---------- */
  /** Heuristic: does this URL look like a video we can fetch with yt-dlp? */
  const looksLikeVideoUrl = (url) => {
    const u = (url || "").toLowerCase();
    if (!u) return false;
    return /youtube\.com|youtu\.be|tiktok\.com|instagram\.com|fb\.watch|facebook\.com\/(?:[^/]+\/)?(?:videos|reel)|twitter\.com|x\.com|vimeo\.com|\.mp4(?:[?#]|$)|\.mov(?:[?#]|$)|\.webm(?:[?#]|$)|\.mkv(?:[?#]|$)/.test(u);
  };
  /** Heuristic: does this URL look like a fundraiser / donation destination? */
  const looksLikeDonationUrl = (url) => {
    const u = (url || "").toLowerCase();
    if (!u) return false;
    return /gofundme|justgiving|donorbox|buy\.stripe|patreon|ko-fi|paypal\.me|paypal\.com\/donate|donate\.|just-giving/.test(u);
  };

  /** Pre-fill the studio with a public submission: title → title field,
   *  message → script, source_url → either fetched as the source video OR
   *  parked as the donation link, category → applied after render. */
  const useSubmission = (s) => {
    if (!s) return;
    setError("");
    if (s.title) setTitle(s.title);
    // Only stomp on existing script/text if it's empty — keep any in-flight
    // work the user already has on screen.
    if (s.message) {
      setText((prev) => (prev && prev.trim() ? `${prev}\n\n${s.message}` : s.message));
    }
    const url = (s.source_url || "").trim();
    let donationFromUrl = "";
    if (url) {
      if (looksLikeVideoUrl(url)) {
        // Drop it into the source-video URL input so the user can hit Fetch
        // (or auto-fetches once we trigger it). We don't auto-fetch here to
        // give the owner a chance to review before bandwidth burns.
        setVideoUrl(url);
        // Switch to dub mode so the URL row is visible.
        setMode("dub");
      } else if (looksLikeDonationUrl(url)) {
        donationFromUrl = url;
      } else {
        // Unknown — assume video first; the owner can re-paste into Donate
        // Now if it's actually a charity link.
        setVideoUrl(url);
        setMode("dub");
      }
    }
    setPendingSubmission({
      submission_id: s.id,
      donation_link: donationFromUrl || null,
      category: s.category || null,
      organization_name: s.name || null,
      video_id: null, // gets set once a render finishes
    });
    // Scroll the studio form into view so the owner sees what got pre-filled.
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { /* ignored */ }
  };

  /* ---------- submit ---------- */
  const handleGenerate = async () => {
    setError("");
    if (mode === "dub" && !text.trim()) return setError("Please provide or transcribe a script first.");

    setSubmitting(true);
    setUploadProgress(0);
    try {
      let endpoint, form;
      if (mode === "dub" || mode === "publish_as_is") {
        if (!videoFile && !videoFetched) {
          setSubmitting(false);
          return setError(mode === "dub"
            ? "Please upload a video file or paste a link to re-dub."
            : "Please upload a video file or paste a link to publish.");
        }
        // If the user pasted a URL we already have an upload_id server-side
        // (via /fetch-url) — reuse it. Otherwise chunk-upload the local file.
        let upload_id, filename;
        if (videoFetched) {
          upload_id = videoFetched.upload_id;
          filename = videoFetched.filename;
        } else {
          // Chunked upload avoids the nginx 413 on large videos
          const up = await uploadFileInChunks(videoFile, setUploadProgress);
          upload_id = up.upload_id;
          filename = up.filename;
        }
        // Auto-detect source language from the upload. Runs once per upload
        // and updates `language` state via setLanguage so the form submit
        // below picks up the corrected value (closures capture state by ref
        // through useRef-like pattern; we reach into `setLanguage` then
        // immediately use the returned value to avoid a stale-state race).
        const detected = await detectSourceLang(upload_id, filename);
        const sourceLanguage = detected || language;

        // Upload any tail videos (file or already-fetched URLs)
        const tailMetas = [];
        for (let i = 0; i < tails.length; i++) {
          const t = tails[i];
          if (t.file) {
            setUploadProgress(0);
            const m = await uploadFileInChunks(t.file, setUploadProgress);
            tailMetas.push(m);
          } else if (t.fetched) {
            tailMetas.push({ upload_id: t.fetched.upload_id, filename: t.fetched.filename });
          }
        }

        form = new FormData();
        form.append("language", sourceLanguage);
        form.append("subtitles", subtitles ? "true" : "false");
        form.append("upload_id", upload_id);
        form.append("filename", filename);
        form.append("branded_endcard", brandedEndcard ? "true" : "false");
        if (tailMetas.length > 0) form.append("tail_uploads", JSON.stringify(tailMetas));

        if (mode === "dub") {
          const langVoices = voicesByLang[sourceLanguage] || [];
          const matchingVoice = langVoices.find((v) => v.gender === voiceGender) || langVoices[0];
          const primaryVoice = matchingVoice?.id || voice;
          form.append("text", text);
          form.append("voice", primaryVoice);
          form.append("title", title || "Untitled Dub");
          form.append("gender", voiceGender);
          endpoint = `${API}/dub-from-upload`;
        } else {
          form.append("title", title || "Untitled Story");
          form.append("transcribe_for_seo", transcribeForSeo ? "true" : "false");
          form.append("tillo_episode", tilloEpisode ? "true" : "false");
          if (tilloEpisode) {
            form.append("tillo_song_title", tilloSongTitle);
            form.append("tillo_lyrics", tilloLyrics);
          }
          endpoint = `${API}/publish-as-is`;
        }
      } else {
        if (images.length === 0) { setSubmitting(false); return setError("Please upload at least one image."); }
        const langVoices = voicesByLang[language] || [];
        const matchingVoice = langVoices.find((v) => v.gender === voiceGender) || langVoices[0];
        const primaryVoice = matchingVoice?.id || voice;
        form = new FormData();
        form.append("text", text);
        form.append("language", language);
        form.append("voice", primaryVoice);
        form.append("subtitles", subtitles ? "true" : "false");
        form.append("title", title || "Untitled Narration");
        images.forEach((img) => form.append("images", img.file));
        endpoint = `${API}/generate`;
      }

      // Retry momentary network drops (server busy with a heavy scan or a
      // pod restart). Safe: dub/publish endpoints are idempotent per
      // upload_id server-side. /generate isn't, so it never retries.
      const canRetry = endpoint !== `${API}/generate`;
      let data;
      for (let attempt = 0; ; attempt++) {
        try {
          ({ data } = await axios.post(endpoint, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 10 * 60 * 1000,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
          }));
          break;
        } catch (err) {
          if (canRetry && !err.response && attempt < 2) {
            await new Promise((r) => setTimeout(r, 3000 * (attempt + 1)));
            continue;
          }
          throw err;
        }
      }
      setCurrentJob({
        id: data.id, status: "queued", progress: 5,
        message: "Queued…", language,
        language_name: languages.find((l) => l.code === language)?.name,
        voice, subtitles, title: title || "Untitled",
      });
      pollJob(data.id);
    } catch (e) {
      console.error("Generation error:", e);
      const parts = [];
      if (e.response?.status) parts.push(`HTTP ${e.response.status}`);
      if (e.response?.data?.detail) parts.push(e.response.data.detail);
      else if (e.response?.data && typeof e.response.data === "string") parts.push(e.response.data.slice(0, 150));
      else if (e.code) parts.push(e.code);
      else if (e.message) parts.push(e.message);
      setError(`Failed to start generation — ${parts.join(" · ") || "unknown error"}`);
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const deleteVideo = async (id) => {
    try {
      await axios.delete(`${API}/videos/${id}`);
      setLibrary((prev) => prev.filter((v) => v.id !== id));
      if (currentJob?.id === id) setCurrentJob(null);
    } catch (e) { console.error(e); }
  };

  // Prompt for the admin token once per session and cache it in
  // localStorage so subsequent admin actions don't re-prompt. Returns
  // null if the user cancels the prompt (so callers can bail cleanly).
  //
  // Validates the token is safe to send as an HTTP header value — a
  // multi-line story title accidentally pasted into the prompt would
  // otherwise poison the cache and break every future admin action
  // with "Failed to execute 'setRequestHeader'". If the cached value
  // is invalid we clear it and re-prompt.
  const _isValidHeaderValue = (s) =>
    typeof s === "string" &&
    s.length > 0 &&
    s.length <= 200 &&
    // HTTP header values must be visible ASCII (0x20–0x7E) plus tab.
    // No newlines, no unicode, no control characters.
    /^[\x20-\x7E]+$/.test(s);

  const _getAdminToken = () => {
    let cached = (typeof window !== "undefined" && window.localStorage.getItem("storylo_admin_token")) || "";
    if (cached && !_isValidHeaderValue(cached)) {
      // Poisoned cache (someone typed a title into the admin-token
      // prompt). Wipe and re-prompt.
      window.localStorage.removeItem("storylo_admin_token");
      cached = "";
    }
    if (cached) return cached;
    const tok = window.prompt(
      "Storylo admin token (this is NOT the story title — it's the operator secret. Default: storylo-admin):",
      "storylo-admin",
    );
    if (!tok) return null;
    const trimmed = tok.trim();
    if (!_isValidHeaderValue(trimmed)) {
      window.alert(
        "That doesn't look like an admin token — it contains characters " +
        "that can't be sent in an HTTP header (like line breaks). Try again " +
        "with just the token secret, e.g. 'storylo-admin'.",
      );
      return null;
    }
    window.localStorage.setItem("storylo_admin_token", trimmed);
    return trimmed;
  };

  // ── Studio: inline rename of a single story title ──────────────────
  const editTitle = async (v) => {
    const tok = _getAdminToken();
    if (!tok) return;
    const next = window.prompt("Rename this story to:", v.title || "");
    if (next == null) return;                                  // cancelled
    const trimmed = next.trim();
    if (!trimmed || trimmed === (v.title || "")) return;       // no-op
    try {
      await axios.patch(
        `${API}/admin/videos/${v.id}/title`,
        { title: trimmed },
        { headers: { "X-Admin-Token": tok } },
      );
      const lib = await axios.get(`${API}/videos`);
      setLibrary(lib.data);
      if (currentJob?.id === v.id) setCurrentJob((j) => ({ ...j, title: trimmed }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        window.localStorage.removeItem("storylo_admin_token");
        window.alert("Admin token was rejected. Please try again.");
      } else {
        window.alert(`Rename failed: ${e?.response?.data?.detail || e.message}`);
      }
    }
  };

  // ── Studio: batch-reorder episodes for the RSS feed (Spotify play order)
  // Opens a modal listing every published story. Operator moves rows
  // up/down until the sequence matches how it should play, then Save
  // POSTs the ordered id list to /admin/reorder-videos. Titles are
  // never touched.
  const openReorderModal = async () => {
    const tok = _getAdminToken();
    if (!tok) return;
    try {
      const { data } = await axios.get(
        `${API}/admin/videos-for-pubdate`,
        { headers: { "X-Admin-Token": tok } },
      );
      // Backend returns newest-first (sorted DESC on created_at).
      // Spotify plays OLDEST first, so reverse for a natural "top of
      // list = plays first" reading order in the modal.
      const rows = (data?.stories || []).slice().reverse();
      setReorderRows(rows);
      setShowReorder(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        window.localStorage.removeItem("storylo_admin_token");
        window.alert("Admin token was rejected. Please try again.");
      } else {
        window.alert(`Couldn't load stories: ${e?.response?.data?.detail || e.message}`);
      }
    }
  };

  const moveReorderRow = (idx, dir) => {
    setReorderRows((prev) => {
      const nxt = prev.slice();
      const j = idx + dir;
      if (j < 0 || j >= nxt.length) return prev;
      [nxt[idx], nxt[j]] = [nxt[j], nxt[idx]];
      return nxt;
    });
  };

  const saveReorder = async () => {
    const tok = _getAdminToken();
    if (!tok) return;
    try {
      const ids = reorderRows.map((r) => r.id);
      await axios.post(
        `${API}/admin/reorder-videos`,
        { order: ids },
        { headers: { "X-Admin-Token": tok } },
      );
      setShowReorder(false);
      const lib = await axios.get(`${API}/videos`);
      setLibrary(lib.data);
      window.alert("Order saved. Spotify and Apple will pick up the new sequence at their next feed poll (~15–30 min).");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        window.localStorage.removeItem("storylo_admin_token");
        window.alert("Admin token was rejected. Please try again.");
      } else {
        window.alert(`Reorder failed: ${e?.response?.data?.detail || e.message}`);
      }
    }
  };

  const renameAllBalkans = async () => {
    const tok = _getAdminToken();
    if (!tok) return;
    try {
      const { data: preview } = await axios.get(
        `${API}/admin/rename-balkans/preview`,
        { headers: { "X-Admin-Token": tok } },
      );
      const proposed = preview?.proposed || [];
      if (proposed.length === 0) {
        window.alert("Nothing to rename — all Balkans episodes already use the 'NN - Balkans - …' format.");
        return;
      }
      const summary = proposed
        .map((p) => `  ${p.old_title.slice(0, 60)}\n→ ${p.new_title.slice(0, 60)}`)
        .join("\n\n");
      const confirmed = window.confirm(
        `Rename ${proposed.length} Balkans episode${proposed.length === 1 ? "" : "s"}?\n\n${summary}\n\nSpotify and Apple will pick up the new titles at their next feed poll.`,
      );
      if (!confirmed) return;
      const { data: applied } = await axios.post(
        `${API}/admin/rename-balkans/apply`, null,
        { headers: { "X-Admin-Token": tok } },
      );
      const lib = await axios.get(`${API}/videos`);
      setLibrary(lib.data);
      window.alert(`Renamed ${applied?.count || 0} episode${applied?.count === 1 ? "" : "s"}.`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        window.localStorage.removeItem("storylo_admin_token");
        window.alert("Admin token was rejected. Please try again.");
      } else {
        window.alert(`Rename failed: ${e?.response?.data?.detail || e.message}`);
      }
    }
  };

  const videoReady = currentJob?.status === "done" && currentJob?.video_url;
  // ── Dual-picker preview URL resolution (mirrors PublicViewer) ─────
  const sourceLang = currentJob?.language;
  const previewAudio = previewAudioLang || sourceLang;
  const previewSub = previewSubLang || "off";
  const previewIsAudioSource = !!currentJob && previewAudio === sourceLang;
  const previewAudioEntry = currentJob?.languages?.[previewAudio];
  const previewSubEntry = (previewSub && previewSub !== "off")
    ? currentJob?.languages?.[previewSub]
    : null;
  // Active lang for transcript display & VTT srcLang attribution
  const activePreviewLang = (previewSub && previewSub !== "off") ? previewSub : previewAudio;
  // Resolve the actual MP4 to play. Source-audio + subs => subbed variant
  // of the sub-lang (burned in). Source-audio + no subs => raw original
  // upload (real speaker's voice, NOT the same-language dub).
  // Dub audio => the dub video_url, with VTT overlay if subs are set.
  const previewSourceMedia = currentJob?.original_with_tails_url || currentJob?.original_source_url || currentJob?.video_url;
  const currentVideoUrl = (() => {
    if (!currentJob) return null;
    if (previewIsAudioSource) {
      if (previewSub && previewSub !== "off") {
        return previewSubEntry?.subbed_video_url || previewSourceMedia;
      }
      return previewSourceMedia;
    }
    if (previewAudioEntry?.status === "done" && previewAudioEntry.video_url) {
      return previewAudioEntry.video_url;
    }
    return previewSourceMedia;
  })();
  // VTT only when audio is a DUB (not source — source uses burned-in subs).
  const previewVttUrl = (
    currentJob && !previewIsAudioSource && previewSub && previewSub !== "off"
      && previewAudioEntry?.status === "done"
  )
    ? mediaUrl(`/api/media/subtitles/${currentJob.id}_${previewSub}.vtt`)
    : null;
  const previewAudioName = languages.find((l) => l.code === previewAudio)?.name || sourceLang;
  const previewSubName = (previewSub && previewSub !== "off")
    ? (languages.find((l) => l.code === previewSub)?.name || previewSub)
    : "Off";
  const previewAudioStatus = previewAudioEntry?.status;
  const previewSubStatus = previewSubEntry?.status;
  // Human-readable status pill under the title strip.
  const previewSubtitleLabel = (() => {
    if (!currentJob) return "";
    if (!previewIsAudioSource && previewAudioStatus === "processing") {
      return `Preparing ${previewAudioName} dub…`;
    }
    if (!previewIsAudioSource && previewAudioStatus === "failed") {
      return `${previewAudioName} build failed`;
    }
    if (previewSub === "off") return "Subtitles off";
    if (previewIsAudioSource && previewSubEntry?.subbed_video_url) {
      return `Subtitles burned in (${previewSubName})`;
    }
    if (previewIsAudioSource && previewSubStatus === "processing") {
      return `Building ${previewSubName} subtitled variant…`;
    }
    if (previewIsAudioSource && previewSubEntry?.status === "done" && !previewSubEntry?.subbed_video_url) {
      return `${previewSubName} subs queued — refresh in ~30s`;
    }
    if (previewVttUrl) return `${previewSubName} captions overlay`;
    return `${previewSubName} subs`;
  })();
  // Publish-as-is never depends on the transcription, so an in-flight
  // transcribe (e.g. started in dub mode before switching) must not
  // block the Publish button.
  const canSubmit = !submitting &&
    (mode === "publish_as_is"
      ? (!!videoFile || !!videoFetched)
      : !transcribing && text.trim().length > 0 && (mode === "dub" ? (!!videoFile || !!videoFetched) : images.length > 0));

  return (
    <div className="App" data-testid="app-root">
      <header
        className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/5"
        style={{ background: "rgba(9,10,12,0.75)" }}
        data-testid="app-header"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-start justify-between gap-6">
          <div className="flex items-center gap-3 shrink-0">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-xl leading-none">Storylo Studio</div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                Cinematic voiceover · 14 languages
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
            <div className="hidden xl:flex items-center gap-2 text-xs text-[color:var(--muted)]">
              <Sparkles className="w-3.5 h-3.5 text-[color:var(--accent)]" />
              <span>Neural voices · local Whisper</span>
            </div>
            <SubmissionsPanel onUseSubmission={useSubmission} />
            <DonationLinksPanel
              library={library}
              reloadLibrary={async () => {
                try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
              }}
            />
            <BatchAutoWritePanel
              reloadLibrary={async () => {
                try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
              }}
            />
            <BatchPretranslatePanel
              reloadLibrary={async () => {
                try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
              }}
            />
            <PromoReelPanel />
            <TaxonomyBatchPanel />
            <HealLanguagesButton />
            <ThumbHealButton />
            <TilloPanel />
            <VoicesPanel />
            <FixSeriesOrderPanel
              onApplied={async () => {
                try {
                  const lib = await axios.get(`${API}/videos`);
                  setLibrary(lib.data);
                } catch (_e) { /* ignored */ }
              }}
            />
            <RecoverStrandedButton />
            <BackfillDurationsButton />
            <TranscriptBackfillButton />
            <TractionPanel />
            <ShortLinksPanel />
            <button
              onClick={() => setWizardOpen(true)}
              className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-[color:var(--accent)]/30 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 transition-colors inline-flex items-center gap-1.5"
              data-testid="studio-open-wizard"
              title="Step-by-step guided publishing — preview only"
            >
              <Sparkles className="w-3.5 h-3.5" /> Try guided mode
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <section className="mb-10 fade-in">
          <div className="label-eyebrow mb-4">Studio</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-3xl">
            {mode === "publish_as_is"
              ? <>Publish a video <em className="text-[color:var(--accent)] not-italic">with its original audio</em>.</>
              : <>Re-voice your films in a <em className="text-[color:var(--accent)] not-italic">new language</em>.</>}
          </h1>
          <p className="text-[color:var(--muted)] mt-5 max-w-2xl leading-relaxed">
            {mode === "publish_as_is"
              ? "Drop in your finished video — we'll publish it to the gallery exactly as you uploaded it. No transcription, no translation, no AI voice. Tail clips and articles still work."
              : "Drop in your finished video and the original narration — we transcribe it, translate it, and render a cinematic dub in the language you choose."}
          </p>
        </section>

        {/* Mode switcher */}
        <div className="mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
             style={{ background: "var(--surface-1)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <VideoIcon className="w-4 h-4 text-[color:var(--accent)]" />
          <span className="text-[color:var(--muted)]">Mode:</span>
          <button
            type="button"
            onClick={() => setMode("dub")}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${mode === "dub" ? "bg-[color:var(--accent)]/20 text-[color:var(--accent)]" : "text-[color:var(--muted)] hover:text-white"}`}
            data-testid="mode-dub"
          >
            Re-dub a video
          </button>
          <button
            type="button"
            onClick={() => setMode("publish_as_is")}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${mode === "publish_as_is" ? "bg-[color:var(--accent)]/20 text-[color:var(--accent)]" : "text-[color:var(--muted)] hover:text-white"}`}
            data-testid="mode-publish-as-is"
            title="Upload a video and keep the original audio — no AI dub"
          >
            Publish as-is
          </button>
          <button
            type="button"
            onClick={() => setMode("script_to_audio")}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${mode === "script_to_audio" ? "bg-[color:var(--accent)]/20 text-[color:var(--accent)]" : "text-[color:var(--muted)] hover:text-white"}`}
            data-testid="mode-script-to-audio"
            title="Just generate a narration MP3 from a script — no video."
          >
            Script → Audio
          </button>
          <button
            type="button"
            onClick={() => setMode("multi_part")}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${mode === "multi_part" ? "bg-[color:var(--accent)]/20 text-[color:var(--accent)]" : "text-[color:var(--muted)] hover:text-white"}`}
            data-testid="mode-multi-part"
            title="Break a long script into 3-min chunks for Opus, then stitch the animated MP4s back into one video"
          >
            Multi-part (Opus)
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* LEFT */}
          <section className="lg:col-span-3 space-y-6 stagger">
            {mode === "script_to_audio" ? (
              <ScriptToAudioPanel />
            ) : mode === "multi_part" ? (
              <MultiPartBuilderPanel />
            ) : mode === "dub" || mode === "publish_as_is" ? (
              <>
                {/* Video drop */}
                <div className="panel p-6 md:p-8" data-testid="video-upload-zone">
                  <div className="label-eyebrow mb-4">1 · Source Video</div>
                  <div
                    className="dropzone p-8 flex flex-col items-center text-center"
                    onClick={() => videoInputRef.current?.click()}
                    data-testid="video-dropzone"
                  >
                    {videoPreview ? (
                      <video src={videoPreview} controls className="max-h-48 rounded-lg" />
                    ) : videoFetched ? (
                      <div className="flex flex-col items-center">
                        <LinkIcon className="w-7 h-7 text-[color:var(--accent)] mb-2" />
                        <p className="font-display text-lg">Fetched from URL ✓</p>
                        <p className="text-xs text-[color:var(--muted)] mt-1">
                          {(videoFetched.size / 1024 / 1024).toFixed(1)} MB · ready to dub
                        </p>
                      </div>
                    ) : (
                      <>
                        <VideoIcon className="w-7 h-7 text-[color:var(--muted)] mb-3" />
                        <p className="font-display text-lg">Upload your animated video</p>
                        <p className="text-xs text-[color:var(--muted)] mt-1">MP4, MOV, MKV, WebM</p>
                      </>
                    )}
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => onVideoSelect(e.target.files?.[0])}
                      data-testid="video-input"
                    />
                  </div>

                  {/* …or paste a link (YouTube / TikTok / IG / X / direct mp4) */}
                  <div className="mt-4">
                    <div className="flex items-center gap-2 my-2">
                      <div className="flex-1 h-px bg-white/10"></div>
                      <span className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)]">or paste a link</span>
                      <div className="flex-1 h-px bg-white/10"></div>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="url"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="Paste a video URL — TikTok, Instagram, X, Vimeo, MP4, or YouTube"
                        className="input-base flex-1 px-3 py-2 text-sm"
                        data-testid="video-url-input"
                      />
                      <button
                        onClick={fetchSourceVideoUrl}
                        disabled={!videoUrl.trim() || videoFetching}
                        className="btn-ghost text-sm whitespace-nowrap flex items-center gap-2"
                        data-testid="video-url-fetch"
                      >
                        {videoFetching ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching…</> : "Fetch"}
                      </button>
                      <YouTubeImportButton
                        onFileSelected={onVideoSelect}
                        onFetched={(data) => {
                          // Mirror the success path in fetchSourceVideoUrl —
                          // populate the parent's video state from the
                          // auto-download payload so the rest of Studio
                          // (script extract, dub, publish) continues
                          // exactly as if the user had pasted the URL.
                          setVideoFile(null);
                          setVideoFetched(data);
                          setVideoUrl("");
                          if (videoPreview) URL.revokeObjectURL(videoPreview);
                          setVideoPreview(null);
                        }}
                      />
                    </div>
                    <p className="text-[11px] text-[color:var(--muted)] mt-2 leading-relaxed">
                      We download with yt-dlp, transcribe with Whisper, then dub. <strong className="text-white">Works directly</strong> with TikTok, Instagram, X, Vimeo, and any direct MP4 link. <strong className="text-amber-300">YouTube &amp; Facebook</strong> block downloads from cloud servers — for those, save the MP4 to your computer first using any reliable downloader, then drag the file into the upload box above.
                    </p>
                  </div>

                  {(videoFile || videoFetched) && (
                    <div className="flex items-center justify-between mt-3 text-xs text-[color:var(--muted)]">
                      <span data-testid="video-filename">{videoFile?.name || videoFetched?.filename}</span>
                      <button onClick={clearSourceVideo} className="hover:text-red-400" data-testid="video-clear">Clear</button>
                    </div>
                  )}

                  {/* Source language picker. Defaults to English but the user
                      can override before submit. After upload, Whisper auto-
                      detects the spoken language and flips this dropdown,
                      surfacing a "Detected" chip the user can review.
                      Critical for the entire dub pipeline — picking the wrong
                      source language causes the "English render plays
                      German audio" bug. */}
                  <div className="mt-5">
                    <label className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--muted)]">
                      Source language
                    </label>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <select
                        value={language}
                        onChange={(e) => {
                          setLanguage(e.target.value);
                          setDetectedLang((d) => d ? { ...d, confirmed: d.language === e.target.value } : d);
                        }}
                        className="input-base px-3 py-2 text-sm flex-1 min-w-[180px]"
                        data-testid="source-language-picker"
                      >
                        {languages.map((l) => (
                          <option key={l.code} value={l.code}>
                            {LANGUAGE_FLAG[l.code] || ""} {l.name}
                          </option>
                        ))}
                      </select>
                      {detectedLang && (
                        <span
                          className={`text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-md inline-flex items-center gap-1.5 ${
                            detectedLang.language === language
                              ? "text-emerald-300 border border-emerald-500/30 bg-emerald-500/5"
                              : "text-amber-300 border border-amber-500/30 bg-amber-500/5"
                          }`}
                          data-testid="source-language-detected"
                          title={`Whisper detected: ${detectedLang.raw}`}
                        >
                          {LANGUAGE_FLAG[detectedLang.language] || ""} Detected: {detectedLang.language_name}
                          {detectedLang.language === language ? " ✓" : " — looks right?"}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-[color:var(--muted)] mt-1.5 leading-relaxed">
                      The language your video is narrated in. We translate from this into all 14 languages for the player. Picking the wrong one breaks every other language.
                    </p>
                  </div>

                  {/* Use built-in voiceover toggle — only relevant in dub mode where we need a script */}
                  {mode === "dub" && (
                  <label
                    className="mt-5 flex items-start gap-3 cursor-pointer rounded-lg p-3 hover:bg-white/5 transition-colors"
                    data-testid="use-video-voiceover"
                  >
                    <input
                      type="checkbox"
                      checked={useVideoVoiceover}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setUseVideoVoiceover(next);
                        if (next && !text.trim() && !transcribing) {
                          if (videoFile) transcribeFromVideo(videoFile);
                          else if (videoFetched) transcribeFromVideo(null, videoFetched);
                        }
                      }}
                      className="mt-0.5 w-4 h-4 accent-[color:var(--accent)]"
                      data-testid="use-video-voiceover-checkbox"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Get the script from this video's audio</div>
                      <div className="text-[11px] text-[color:var(--muted)] mt-0.5 leading-relaxed">
                        Skip the separate audio upload — we'll extract the narration from the video itself and transcribe it for translation. Note: Storylo always re-narrates with the AI voice you pick below. Want to keep the original audio? Switch the Mode at the top to <strong className="text-white">Publish as-is</strong>.
                      </div>
                    </div>
                  </label>
                  )}

                  {/* Publish-as-is helper note: makes it crystal clear no AI dub will run */}
                  {mode === "publish_as_is" && (
                    <div className="mt-5 rounded-lg p-4"
                         style={{ background: "rgba(16, 185, 129, 0.07)", border: "1px solid rgba(16, 185, 129, 0.25)" }}
                         data-testid="publish-as-is-note">
                      <div className="text-[11px] tracking-[0.22em] uppercase mb-1" style={{ color: "#10b981" }}>
                        ▸ Publish as-is
                      </div>
                      <div className="text-xs text-[color:var(--muted)] leading-relaxed">
                        Storylo will publish your video <strong className="text-white">with its original audio</strong> — no transcription, no translation, no AI voice. You can still add tail clips below (each keeps its own audio) and pick a language tag for the gallery.
                      </div>

                      {/* SEO / multi-language opt-in: keep the original audio,
                          but transcribe in the background so translation chips
                          on /s/{slug} work in all 10 languages. */}
                      <label
                        className="mt-4 flex items-start gap-3 cursor-pointer rounded-md p-2 -mx-1 hover:bg-white/[0.04] transition-colors"
                        data-testid="transcribe-for-seo-row"
                      >
                        <input
                          type="checkbox"
                          checked={transcribeForSeo}
                          onChange={(e) => setTranscribeForSeo(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-emerald-500"
                          data-testid="transcribe-for-seo-checkbox"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white flex items-center gap-2">
                            🌍 Auto-transcribe for translations
                          </div>
                          <div className="text-[11px] text-[color:var(--muted)] mt-0.5 leading-relaxed">
                            After we publish, run Whisper on the audio in the background so viewers in German, Spanish, French, etc. still see your story translated. The video itself stays untouched — original voice, original audio.
                          </div>
                        </div>
                      </label>

                      {/* Tillo opt-in: when ticked the finished video lands
                          on the kids tab automatically with its Sing-Along
                          lyrics page — no Tillo panel needed. */}
                      <label
                        className="mt-2 flex items-start gap-3 cursor-pointer rounded-md p-2 -mx-1 hover:bg-white/[0.04] transition-colors"
                        data-testid="tillo-episode-row"
                      >
                        <input
                          type="checkbox"
                          checked={tilloEpisode}
                          onChange={(e) => setTilloEpisode(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-amber-400"
                          data-testid="tillo-episode-checkbox"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white flex items-center gap-2">
                            ⭐ Tillo episode (Kids section)
                          </div>
                          <div className="text-[11px] text-[color:var(--muted)] mt-0.5 leading-relaxed">
                            When it finishes, this story appears on storylo.co.uk/kids automatically — with a Sing-Along page for the song lyrics below.
                          </div>
                          {tilloEpisode && (
                            <div className="mt-3 space-y-2" onClick={(e) => e.preventDefault()}>
                              <input
                                value={tilloSongTitle}
                                onChange={(e) => setTilloSongTitle(e.target.value)}
                                placeholder="Song title (e.g. Little Lantern)"
                                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm"
                                data-testid="tillo-song-title-input"
                              />
                              <textarea
                                value={tilloLyrics}
                                onChange={(e) => setTilloLyrics(e.target.value)}
                                placeholder="Song lyrics — leave a blank line between verses"
                                rows={6}
                                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm"
                                data-testid="tillo-lyrics-input"
                              />
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                {/* Optional tail videos (multiple) */}
                <div className="panel p-6 md:p-8">
                  <div className="flex items-center justify-between mb-5">
                    <div className="label-eyebrow">2 · Tail videos <span className="normal-case tracking-normal text-[color:var(--muted)] font-normal ml-1">(optional · each keeps its own audio · crossfaded in order)</span></div>
                  </div>

                  <div className="space-y-4">
                    {tails.map((t, idx) => (
                      <div key={t.id} className="rounded-xl border border-white/5 p-4" style={{ background: "var(--surface-2)" }} data-testid={`tail-${idx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-[color:var(--muted)] uppercase tracking-[0.2em]">Clip {idx + 1}</span>
                          {tails.length > 1 && (
                            <button
                              onClick={() => removeTail(t.id)}
                              className="text-[color:var(--muted)] hover:text-red-400 text-xs flex items-center gap-1"
                              data-testid={`tail-remove-${idx}`}
                            >
                              <X className="w-3.5 h-3.5" /> Remove
                            </button>
                          )}
                        </div>

                        <div
                          className="dropzone p-5 flex flex-col items-center text-center"
                          onClick={() => tailInputRefs.current[t.id]?.click()}
                          data-testid={`tail-dropzone-${idx}`}
                        >
                          {t.preview ? (
                            t.file && t.file.type?.startsWith("image/") ? (
                              <img src={t.preview} alt="still" className="max-h-36 rounded-lg" />
                            ) : (
                              <video src={t.preview} controls className="max-h-36 rounded-lg" />
                            )
                          ) : t.fetched ? (
                            <div className="flex flex-col items-center">
                              <UserRound className="w-6 h-6 text-[color:var(--accent)] mb-2" />
                              <p className="font-display text-base">Fetched from URL ✓</p>
                              <p className="text-xs text-[color:var(--muted)] mt-1">
                                {(t.fetched.size / 1024 / 1024).toFixed(1)} MB · ready
                              </p>
                            </div>
                          ) : (
                            <>
                              <UserRound className="w-6 h-6 text-[color:var(--muted)] mb-2" />
                              <p className="font-display text-base">Upload videos or photos</p>
                              <p className="text-xs text-[color:var(--muted)] mt-1">
                                Pick many at once · photos hold for 4s with a gentle zoom · everything crossfades
                              </p>
                            </>
                          )}
                          <input
                            ref={(el) => { if (el) tailInputRefs.current[t.id] = el; }}
                            type="file"
                            accept="video/*,image/jpeg,image/png,image/webp,image/heic,image/heif"
                            multiple
                            className="hidden"
                            onChange={(e) => onTailFilesBulk(t.id, e.target.files)}
                            data-testid={`tail-input-${idx}`}
                          />
                        </div>

                        <div className="mt-3">
                          <div className="flex items-center gap-2 my-2">
                            <div className="flex-1 h-px bg-white/10"></div>
                            <span className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)]">or paste a link</span>
                            <div className="flex-1 h-px bg-white/10"></div>
                          </div>
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="url"
                              value={t.url}
                              onChange={(e) => updateTail(t.id, { url: e.target.value })}
                              placeholder="Paste a video URL — TikTok, Instagram, X, Vimeo, MP4, or YouTube"
                              className="input-base flex-1 px-3 py-2 text-sm"
                              data-testid={`tail-url-${idx}`}
                            />
                            <button
                              onClick={() => fetchTailUrl(t.id)}
                              disabled={!t.url.trim() || t.fetching}
                              className="btn-ghost text-sm whitespace-nowrap flex items-center gap-2"
                              data-testid={`tail-fetch-${idx}`}
                            >
                              {t.fetching ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching…</> : "Fetch"}
                            </button>
                            <YouTubeImportButton
                              onFileSelected={(file) => {
                                if (t.preview) URL.revokeObjectURL(t.preview);
                                updateTail(t.id, {
                                  file,
                                  preview: URL.createObjectURL(file),
                                  fetched: null,
                                  url: "",
                                  transcript: "",
                                  transcriptInserted: false,
                                  transcribeMessage: "",
                                });
                              }}
                              onFetched={(data) => {
                                if (t.preview) URL.revokeObjectURL(t.preview);
                                updateTail(t.id, {
                                  fetched: data,
                                  file: null,
                                  preview: null,
                                  url: "",
                                  transcript: "",
                                  transcriptInserted: false,
                                  transcribeMessage: "",
                                });
                              }}
                            />
                          </div>
                        </div>

                        {(t.file || t.fetched) && (
                          <div className="flex items-center justify-between mt-3 text-xs text-[color:var(--muted)]">
                            <span data-testid={`tail-name-${idx}`}>{t.file?.name || t.fetched?.filename}</span>
                            <button onClick={() => clearTail(t.id)} className="hover:text-red-400" data-testid={`tail-clear-${idx}`}>Clear</button>
                          </div>
                        )}

                        {/* Audio → transcript helper. Available whenever the
                            slot has a usable source: a local file, an already-
                            fetched URL, or a typed URL we can fetch on-the-fly. */}
                        {(t.file || t.fetched || (t.url || "").trim()) && (
                          <div className="mt-3">
                            <div className="flex items-center gap-2 my-2">
                              <div className="flex-1 h-px bg-white/10"></div>
                              <span className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)]">audio → script</span>
                              <div className="flex-1 h-px bg-white/10"></div>
                            </div>
                            <button
                              onClick={() => transcribeTail(t.id)}
                              disabled={t.transcribing || t.fetching}
                              className="btn-ghost text-sm w-full flex items-center justify-center gap-2"
                              data-testid={`tail-transcribe-${idx}`}
                            >
                              {t.transcribing ? (
                                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t.transcribeMessage || "Transcribing…"}</>
                              ) : (
                                <><Mic className="w-3.5 h-3.5" /> Transcribe this clip's audio</>
                              )}
                            </button>
                            {t.transcript && (
                              <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`tail-transcript-${idx}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)]">Transcript preview</span>
                                  {t.transcriptInserted && (
                                    <span className="text-[10px] text-[color:var(--accent)] flex items-center gap-1">
                                      <Check className="w-3 h-3" /> Inserted
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-white/80 whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">
                                  {t.transcript}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                  <button
                                    onClick={() => insertTailTranscript(t.id, "append")}
                                    className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5"
                                    data-testid={`tail-transcript-append-${idx}`}
                                  >
                                    <Sparkles className="w-3 h-3" /> Append to script
                                  </button>
                                  <button
                                    onClick={() => insertTailTranscript(t.id, "replace")}
                                    className="btn-ghost text-xs px-3 py-1.5"
                                    data-testid={`tail-transcript-replace-${idx}`}
                                  >
                                    Replace script
                                  </button>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard?.writeText(t.transcript || "");
                                    }}
                                    className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5"
                                    data-testid={`tail-transcript-copy-${idx}`}
                                  >
                                    <Copy className="w-3 h-3" /> Copy
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={addTail}
                    className="mt-4 btn-ghost text-sm flex items-center gap-2"
                    data-testid="tail-add"
                  >
                    <Sparkles className="w-4 h-4" /> Add another tail clip
                  </button>
                  <p className="text-[11px] text-[color:var(--muted)] mt-3">
                    Tip: clips play in the order shown. <strong className="text-white">Drop multiple photos / videos at once</strong> and they'll fill new slots automatically — there's no limit. You can also mix uploaded files with URL-fetched clips (TikTok, Instagram, X/Twitter, Vimeo, direct MP4 links all work). <strong className="text-amber-300">YouTube &amp; Facebook</strong> block downloads from cloud servers — save those clips to your computer first, then drop them in.
                  </p>
                </div>

                {/* Audio drop — only when not extracting from video, and only in dub mode */}
                {mode === "dub" && !useVideoVoiceover && (
                <div className="panel p-6 md:p-8" data-testid="audio-upload-zone">
                  <div className="flex items-center justify-between mb-4">
                    <div className="label-eyebrow">3 · Original narration (.wav / .mp3)</div>
                    {transcribing && (
                      <span className="text-xs text-[color:var(--accent)] flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {uploadProgress > 0 && uploadProgress < 100 ? `Uploading ${uploadProgress}%` : "Transcribing…"}
                      </span>
                    )}
                  </div>
                  <div
                    className="dropzone p-6 flex flex-col items-center text-center"
                    onClick={() => audioInputRef.current?.click()}
                    data-testid="audio-dropzone"
                  >
                    <FileAudio className="w-7 h-7 text-[color:var(--muted)] mb-3" />
                    <p className="font-display text-lg">Upload audio — we'll transcribe it</p>
                    <p className="text-xs text-[color:var(--muted)] mt-1">
                      .wav · .mp3 · .m4a · .mp4 — Whisper extracts the script for you
                    </p>
                    <input
                      ref={audioInputRef}
                      type="file"
                      accept="audio/*,video/mp4"
                      className="hidden"
                      onChange={(e) => onAudioSelect(e.target.files?.[0])}
                      data-testid="audio-input"
                    />
                  </div>
                  {audioFile && (
                    <div className="flex items-center justify-between mt-3 text-xs text-[color:var(--muted)]">
                      <span data-testid="audio-filename">{audioFile.name}</span>
                    </div>
                  )}
                </div>
                )}

                {/* Transcribing-from-video indicator */}
                {useVideoVoiceover && transcribing && (
                  <div className="panel p-5 flex items-center gap-3 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-[color:var(--accent)]" />
                    <span>
                      {uploadProgress > 0 && uploadProgress < 100
                        ? `Uploading the video (${uploadProgress}%) to extract narration…`
                        : "Extracting narration from your video…"}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="panel p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="label-eyebrow">Footage — Images</div>
                  <span className="text-xs text-[color:var(--muted)]" data-testid="image-count">
                    {images.length} {images.length === 1 ? "image" : "images"}
                  </span>
                </div>
                <div
                  className={`dropzone p-8 flex flex-col items-center text-center ${dragOver ? "dragging" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onImageDrop}
                  onClick={() => imgInputRef.current?.click()}
                  data-testid="image-dropzone"
                >
                  <Upload className="w-7 h-7 text-[color:var(--muted)] mb-3" />
                  <p className="font-display text-lg">Drop images or click to browse</p>
                  <p className="text-xs text-[color:var(--muted)] mt-1">JPG · PNG · WebP — played in the order listed below</p>
                  <input
                    ref={imgInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => addImages(e.target.files)}
                    data-testid="image-input"
                  />
                </div>
                {images.length > 0 && (
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="image-grid">
                    {images.map((img, idx) => (
                      <div key={img.id} className="thumb-card group" data-testid={`image-thumb-${idx}`}>
                        <img src={img.preview} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                          <div className="flex justify-end">
                            <button
                              onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                              className="w-7 h-7 rounded-full bg-black/70 hover:bg-red-500/80 flex items-center justify-center"
                              data-testid={`image-remove-${idx}`}
                            ><X className="w-4 h-4" /></button>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); moveImage(idx, -1); }}
                                      className="px-2 py-1 rounded bg-black/70 hover:bg-black/90" data-testid={`image-up-${idx}`}>↑</button>
                              <button onClick={(e) => { e.stopPropagation(); moveImage(idx, 1); }}
                                      className="px-2 py-1 rounded bg-black/70 hover:bg-black/90" data-testid={`image-down-${idx}`}>↓</button>
                            </div>
                            <span className="px-2 py-0.5 rounded bg-[color:var(--accent)] text-black font-semibold">{idx + 1}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Script */}
            <div className="panel p-6 md:p-8">
              {pendingSubmission && (
                <div
                  className="mb-4 rounded-lg p-3 flex items-start gap-2"
                  style={{ background: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.25)" }}
                  data-testid="pending-submission-banner"
                >
                  <Sparkles className="w-4 h-4 text-[color:var(--accent)] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs text-[color:var(--accent)] uppercase tracking-[0.18em] mb-0.5">From submission</div>
                    <div className="text-xs text-[color:var(--muted)] leading-relaxed">
                      Title, script {pendingSubmission.donation_link ? "and donation link" : ""}{pendingSubmission.category ? `${pendingSubmission.donation_link ? "," : " and"} category (${pendingSubmission.category.replace("_", " ")})` : ""} pre-filled. They'll attach automatically once the render completes.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingSubmission(null)}
                    className="text-[color:var(--muted)] hover:text-white text-xs"
                    title="Discard pending metadata"
                    data-testid="pending-submission-clear"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="label-eyebrow">{mode === "dub" ? "4 · Title & Script" : mode === "publish_as_is" ? "Title" : "Script"}</div>
                {mode === "dub" && <div className="text-xs text-[color:var(--muted)] flex items-center gap-1"><Mic className="w-3 h-3" /> Transcribed from your audio — edit if needed</div>}
              </div>
              <input
                type="text"
                placeholder={mode === "dub" ? "Project title — e.g. Atila's Story (German dub)" : mode === "publish_as_is" ? "Story title — shown on the gallery card" : "Project title"}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`input-base w-full px-4 py-3 ${mode === "publish_as_is" ? "" : "mb-3"}`}
                data-testid="input-title"
              />
              {mode !== "publish_as_is" && (
              <>
              <textarea
                id="script-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Script will appear here after transcription. You can also type or paste it manually."
                rows={10}
                className="input-base w-full px-4 py-4 leading-relaxed resize-y"
                data-testid="input-text"
              />
              <div className="flex justify-between items-center mt-2 text-xs text-[color:var(--muted)]">
                <span>{text.length} characters · ~{Math.max(1, Math.round(text.split(/\s+/).filter(Boolean).length / 150))} min at 150 wpm</span>
                {text.trim().length > 0 && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(text);
                          setScriptCopied(true);
                          setTimeout(() => setScriptCopied(false), 1500);
                        } catch (_e) { /* noop */ }
                      }}
                      className="hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1"
                      data-testid="btn-copy-script"
                    >
                      {scriptCopied ? <><Check className="w-3.5 h-3.5" /> Copied</>
                                    : <><Copy className="w-3.5 h-3.5" /> Copy script</>}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${(title || "script").replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "script"}.txt`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      }}
                      className="hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1"
                      data-testid="btn-download-script"
                    >
                      <Download className="w-3.5 h-3.5" /> Download .txt
                    </button>
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            {/* Voice */}
            {mode !== "publish_as_is" && (
            <div className="panel p-6 md:p-8">
              <div className="label-eyebrow mb-5">{mode === "dub" ? "5 · Voice" : "Voice"}</div>
              <div className="grid grid-cols-2 gap-3" data-testid="voice-gender-picker">
                {[
                  { id: "female", label: "Female", subtitle: "Warm · narration" },
                  { id: "male",   label: "Male",   subtitle: "Cinematic · narrator" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setVoiceGender(opt.id)}
                    className={`p-4 rounded-xl text-left transition-all ${
                      voiceGender === opt.id
                        ? "border border-[color:var(--accent)] bg-[color:var(--accent)]/5"
                        : "border border-white/5 hover:border-white/20"
                    }`}
                    data-testid={`voice-gender-${opt.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <UserRound className={`w-4 h-4 ${voiceGender === opt.id ? "text-[color:var(--accent)]" : "text-[color:var(--muted)]"}`} />
                      <div className="font-medium text-sm">{opt.label}</div>
                    </div>
                    <div className="text-[11px] text-[color:var(--muted)]">{opt.subtitle}</div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[color:var(--muted)] mt-3 leading-relaxed">
                Auto-mapped per language: English uses Sonia / Ryan, German uses Katja / Conrad,
                French uses Denise / Henri, and so on. All 14 languages stay consistent with your pick.
              </p>
            </div>
            )}

            {/* Subtitles */}
            <div className="panel p-6 md:p-8">
              <div className="label-eyebrow mb-5">{mode === "dub" ? "6 · Subtitles" : "Subtitles"}</div>
              <div className="flex items-center justify-between p-4 rounded-xl"
                   style={{ background: "var(--surface-2)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-3">
                  <Subtitles className="w-5 h-5 text-[color:var(--accent)]" />
                  <div>
                    <div className="font-medium">Burn-in subtitles</div>
                    <div className="text-xs text-[color:var(--muted)]">Captions burn into every language version. Leave off for a clean cinematic look.</div>
                  </div>
                </div>
                <button
                  onClick={() => setSubtitles(!subtitles)}
                  data-testid="toggle-subtitles"
                  role="switch"
                  aria-checked={subtitles}
                  className={`relative w-12 h-7 rounded-full transition-colors ${subtitles ? "bg-[color:var(--accent)]" : "bg-white/15"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${subtitles ? "translate-x-5" : ""}`} />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl mt-3"
                   style={{ background: "var(--surface-2)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-3">
                  <Film className="w-5 h-5 text-[color:var(--accent)]" />
                  <div>
                    <div className="font-medium">Storylo end-card</div>
                    <div className="text-xs text-[color:var(--muted)]">A 2-second branded sign-off so downloaded videos still credit Storylo when posted on TikTok / Reels / Facebook.</div>
                  </div>
                </div>
                <button
                  onClick={() => setBrandedEndcard(!brandedEndcard)}
                  data-testid="toggle-endcard"
                  role="switch"
                  aria-checked={brandedEndcard}
                  className={`relative w-12 h-7 rounded-full transition-colors ${brandedEndcard ? "bg-[color:var(--accent)]" : "bg-white/15"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${brandedEndcard ? "translate-x-5" : ""}`} />
                </button>
              </div>
              <p className="text-[11px] text-[color:var(--muted)] mt-3 leading-relaxed">
                Every render produces all 14 languages automatically. Viewers pick their language from the player dropdown — no target-language selection needed here.
              </p>
            </div>

            {error && transcribeErrorKind !== "upload_missing" && (
              <div className="panel p-4 border-red-500/40 text-red-300 text-sm" data-testid="error-banner">{error}</div>
            )}
            {transcribeErrorKind === "upload_missing" && (
              <div
                className="panel p-5 border border-amber-500/40 bg-amber-500/[0.06]"
                data-testid="transcribe-recovery-card"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl leading-none mt-0.5">⚠️</div>
                  <div className="flex-1">
                    <div className="font-display text-base text-amber-100">
                      Your uploaded video needs to be re-attached
                    </div>
                    <div className="text-xs text-amber-200/80 mt-1 leading-relaxed">
                      A previous publish or refresh cleaned up the temporary copy of
                      your video, so we can&apos;t transcribe it. Nothing about your
                      settings is lost — just pick how you&apos;d like to continue:
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      setVideoFile(null);
                      setVideoFetched(null);
                      setTranscribeErrorKind(null);
                      setError("");
                      // Scroll the upload zone into view.
                      document
                        .querySelector('[data-testid="video-upload-zone"]')
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    className="text-xs px-3 py-2.5 rounded-lg border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
                    data-testid="recovery-reupload-video"
                  >
                    <div className="font-medium">🔄 Re-upload video</div>
                    <div className="text-[10px] text-[color:var(--muted)] mt-0.5">
                      Drag your MP4 again — everything else is preserved
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setUseVideoVoiceover(false);
                      setTranscribeErrorKind(null);
                      setError("");
                      document
                        .querySelector('[data-testid="audio-upload-zone"]')
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    className="text-xs px-3 py-2.5 rounded-lg border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
                    data-testid="recovery-use-audio-file"
                  >
                    <div className="font-medium">🎧 Upload audio separately</div>
                    <div className="text-[10px] text-[color:var(--muted)] mt-0.5">
                      Attach an .mp3 / .wav / .m4a of just the narration
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setUseVideoVoiceover(false);
                      setTranscribeErrorKind(null);
                      setError("");
                      const el = document.getElementById("script-textarea");
                      el?.scrollIntoView({ behavior: "smooth", block: "center" });
                      setTimeout(() => el?.focus?.(), 500);
                    }}
                    className="text-xs px-3 py-2.5 rounded-lg border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
                    data-testid="recovery-type-script"
                  >
                    <div className="font-medium">⌨️ Type / paste the script</div>
                    <div className="text-[10px] text-[color:var(--muted)] mt-0.5">
                      Fastest if you already have the narration text
                    </div>
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={handleGenerate}
                disabled={!canSubmit}
                className={`btn-primary flex items-center gap-2 ${canSubmit ? "glow-pulse" : ""}`}
                data-testid="btn-generate"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {uploadProgress > 0 && uploadProgress < 100 ? `Uploading ${uploadProgress}%` : "Generating…"}</>
                ) : (<><Sparkles className="w-4 h-4" /> {mode === "dub" ? "Generate dub" : mode === "publish_as_is" ? "Publish as-is" : "Generate video"}</>)}
              </button>
              <span className="text-xs text-[color:var(--muted)]">
                {mode === "dub" ? "Translation + TTS + audio mux" : mode === "publish_as_is" ? (transcribeForSeo ? "No AI dub · keeps original audio · transcribes in background for translations" : "No transcription · no AI dub · keeps original audio") : "Translation + TTS + slideshow render"}
                 · typically 30–90 seconds
              </span>
            </div>
          </section>

          {/* RIGHT: preview */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="panel overflow-hidden">
              <div className="relative bg-black aspect-video flex items-center justify-center" data-testid="preview-player">
                {videoReady ? (
                  <video
                    key={`${currentJob.id}-${previewAudio}-${previewSub}`}
                    ref={previewVideoRef}
                    src={mediaUrl(currentVideoUrl)}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-contain"
                    data-testid="preview-video"
                    onLoadedMetadata={() => {
                      const v = previewVideoRef.current;
                      if (!v) return;
                      for (let i = 0; i < v.textTracks.length; i++) {
                        v.textTracks[i].mode = previewCcOn ? "showing" : "hidden";
                      }
                    }}
                  >
                    {previewVttUrl && (
                      <track
                        kind="subtitles"
                        src={previewVttUrl}
                        srcLang={previewSub}
                        label={previewSubName}
                        default
                      />
                    )}
                  </video>
                ) : currentJob ? (
                  <div className="text-center px-6">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-[color:var(--accent)]" />
                    <div className="font-display text-lg mt-4">{currentJob.message || currentJob.status}</div>
                    <div className="mt-4 h-1.5 w-64 mx-auto rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-[color:var(--accent)] transition-all duration-500"
                           style={{ width: `${currentJob.progress || 0}%` }} data-testid="progress-bar" />
                    </div>
                    <div className="text-xs text-[color:var(--muted)] mt-2">{currentJob.progress || 0}%</div>
                    {/* Stuck-render escape hatch. Renders that sit at the
                        same progress for >60s are almost certainly a dead
                        background task (worker restart, ffmpeg crash).
                        The auto-zombie-sweep recovers them in ~4min, but
                        an impatient operator can mark it failed NOW. */}
                    <button
                      onClick={async () => {
                        if (!window.confirm("Mark this stuck render as failed so you can retry?")) return;
                        try {
                          await axios.post(`${API}/videos/${currentJob.id}/force-fail`);
                          const r = await axios.get(`${API}/videos/${currentJob.id}`);
                          setCurrentJob(r.data);
                          const lib = await axios.get(`${API}/videos`);
                          setLibrary(lib.data);
                        } catch (_err) { /* tolerated */ }
                      }}
                      className="mt-4 text-[10px] uppercase tracking-[0.18em] text-red-300 hover:text-red-200 transition-colors"
                      data-testid="btn-force-fail"
                    >Stuck? Force-fail this render</button>
                  </div>
                ) : (
                  <div className="text-center px-6">
                    <Film className="w-10 h-10 mx-auto text-[color:var(--muted)] opacity-60" />
                    <div className="font-display text-lg mt-4">Your preview will play here</div>
                    <div className="text-xs text-[color:var(--muted)] mt-2">
                      {mode === "dub" ? "Upload a video + audio, pick a language, hit generate." : "Upload images, write your script, hit generate."}
                    </div>
                  </div>
                )}
              </div>
              {currentJob && (
                <div className="p-5 border-t border-white/5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-lg truncate" data-testid="preview-title">{currentJob.title}</div>
                    {currentJob.status === "done" && (
                      <button
                        onClick={async () => {
                          const next = window.prompt("Rename story", currentJob.title || "");
                          if (!next || !next.trim() || next === currentJob.title) return;
                          try {
                            const r = await axios.post(`${API}/videos/${currentJob.id}/rename`, { title: next.trim() });
                            setCurrentJob((j) => ({ ...j, title: r.data.title, slug: r.data.slug }));
                            const lib = await axios.get(`${API}/videos`);
                            setLibrary(lib.data);
                          } catch (_err) { /* noop */ }
                        }}
                        className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] hover:text-[color:var(--accent)] transition-colors flex-shrink-0"
                        data-testid="btn-rename"
                      >Rename</button>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--muted)] mt-1" data-testid="preview-meta">
                    {LANGUAGE_FLAG[previewAudio]} {previewAudioName}
                    {" · "}<span data-testid="preview-subtitle-status">{previewSubtitleLabel}</span>
                  </div>

                  {/* Dual-picker preview controls. The Audio dropdown
                      controls what plays; the Subtitles dropdown is
                      independent. Defaults: Audio = source language
                      (Original voice), Subtitles = creator-set
                      default_subtitle_lang or "off". */}
                  {videoReady && currentJob.languages && (
                    <div className="mt-4 space-y-4">
                      {/* AUDIO picker */}
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] block mb-2">
                          Audio
                        </label>
                        <select
                          value={previewAudio}
                          onChange={async (e) => {
                            const code = e.target.value;
                            setPreviewAudioLang(code);
                            if (code === sourceLang) return;
                            const info = currentJob.languages[code];
                            if (!info || info.status === "pending" || info.status === "failed") {
                              try {
                                await axios.post(`${API}/videos/${currentJob.id}/languages/${code}/prepare`);
                                if (!pollRef.current) pollJob(currentJob.id);
                              } catch (err) { console.error("prepare err", err); }
                            }
                          }}
                          className="input-base w-full px-4 py-2.5 text-sm"
                          data-testid="preview-audio-select"
                        >
                          {languages.map((l) => {
                            const isSource = l.code === sourceLang;
                            const info = currentJob.languages[l.code];
                            const state = isSource ? " · Original voice"
                                      : info?.status === "processing" ? " (preparing…)"
                                      : info?.status === "pending" ? " (click to prepare)"
                                      : info?.status === "failed" ? " (unavailable)"
                                      : info?.status === "done" ? " · Dubbed"
                                      : "";
                            return (
                              <option key={l.code} value={l.code}>
                                {LANGUAGE_FLAG[l.code] || "🏳️"}  {l.name}{state}
                              </option>
                            );
                          })}
                        </select>
                        {!previewIsAudioSource && previewAudioStatus === "processing" && (
                          <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[color:var(--accent)]">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Preparing {previewAudioName} (~100s)
                          </div>
                        )}
                        {!previewIsAudioSource && previewAudioStatus === "failed" && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/videos/${currentJob.id}/languages/${previewAudio}/prepare`);
                                if (!pollRef.current) pollJob(currentJob.id);
                              } catch (_err) { /* tolerated */ }
                            }}
                            className="mt-2 text-[10px] uppercase tracking-[0.18em] text-red-300 hover:text-red-200"
                            data-testid="preview-audio-retry"
                          >Retry build</button>
                        )}
                      </div>

                      {/* SUBTITLES picker */}
                      <div className="pt-4 border-t border-white/5">
                        <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] block mb-2">
                          Subtitles
                        </label>
                        <select
                          value={previewSub}
                          onChange={async (e) => {
                            const code = e.target.value;
                            setPreviewSubLang(code);
                            if (code === "off") return;
                            const info = currentJob.languages[code];
                            // Source audio + sub lang => need subbed variant.
                            if (previewIsAudioSource && info?.status === "done" && !info?.subbed_video_url) {
                              try {
                                await axios.post(`${API}/videos/${currentJob.id}/languages/${code}/build-subbed`);
                                if (!pollRef.current) pollJob(currentJob.id);
                              } catch (_err) { /* tolerated */ }
                            } else if (!info || info.status === "pending" || info.status === "failed") {
                              try {
                                await axios.post(`${API}/videos/${currentJob.id}/languages/${code}/prepare`);
                                if (!pollRef.current) pollJob(currentJob.id);
                              } catch (_err) { /* tolerated */ }
                            }
                          }}
                          className="input-base w-full px-4 py-2.5 text-sm"
                          data-testid="preview-sub-select"
                        >
                          <option value="off">— Off —</option>
                          {languages.map((l) => {
                            if (l.code === sourceLang && previewIsAudioSource) return null;
                            const info = currentJob.languages[l.code];
                            const state = info?.status === "processing" ? " (preparing…)"
                                      : info?.status === "pending" ? " (click to prepare)"
                                      : info?.status === "failed" ? " (unavailable)"
                                      : "";
                            return (
                              <option key={l.code} value={l.code}>
                                {LANGUAGE_FLAG[l.code] || "🏳️"}  {l.name}{state}
                              </option>
                            );
                          })}
                        </select>
                        {previewVttUrl && (
                          <button
                            type="button"
                            onClick={() => {
                              const next = !previewCcOn;
                              setPreviewCcOn(next);
                              const v = previewVideoRef.current;
                              if (v) {
                                for (let i = 0; i < v.textTracks.length; i++) {
                                  v.textTracks[i].mode = next ? "showing" : "hidden";
                                }
                              }
                            }}
                            className={`mt-3 w-full text-xs px-3 py-2 rounded-md border transition-colors ${
                              previewCcOn
                                ? "border-[color:var(--accent)]/60 text-[color:var(--accent)] bg-[color:var(--accent)]/5"
                                : "border-white/10 text-[color:var(--muted)] hover:border-white/30"
                            }`}
                            data-testid="preview-cc-toggle"
                            aria-pressed={previewCcOn}
                          >
                            {previewCcOn ? `Captions on (${previewSubName})` : "Captions off"}
                          </button>
                        )}

                        {/* Per-story DEFAULT subtitle language. When set,
                            new visitors land with this sub-lang pre-
                            selected — useful when the creator knows
                            their audience (e.g. Atila's German story
                            aimed at English-speaking supporters). */}
                        <div className="mt-4 pt-4 border-t border-white/5">
                          <label className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--muted)] block mb-2">
                            Default subtitle language for new visitors
                          </label>
                          <select
                            value={currentJob.default_subtitle_lang || "off"}
                            onChange={async (e) => {
                              const code = e.target.value;
                              try {
                                await axios.patch(`${API}/videos/${currentJob.id}/default-subtitle-lang`, {
                                  lang_code: code === "off" ? null : code,
                                });
                                setCurrentJob((j) => ({
                                  ...j,
                                  default_subtitle_lang: code === "off" ? null : code,
                                }));
                                if (!pollRef.current) pollJob(currentJob.id);
                              } catch (_err) { /* tolerated */ }
                            }}
                            className="input-base w-full px-4 py-2 text-xs"
                            data-testid="preview-default-sub-select"
                          >
                            <option value="off">— Viewer's UI locale (default) —</option>
                            {languages.map((l) => {
                              if (l.code === sourceLang) return null;
                              return (
                                <option key={l.code} value={l.code}>
                                  {LANGUAGE_FLAG[l.code] || "🏳️"}  {l.name}
                                </option>
                              );
                            })}
                          </select>
                          <p className="text-[10px] text-[color:var(--muted)] mt-1 leading-relaxed">
                            Overrides what subtitle language first-time visitors see.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {videoReady && (
                    <>
                      <div className="mt-5 pt-5 border-t border-white/5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2">
                            {currentJob.published ? (
                              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)]">
                                <Globe className="w-3.5 h-3.5" /> Live
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                                <EyeOff className="w-3.5 h-3.5" /> Draft
                              </span>
                            )}
                          </div>
                          <button
                            onClick={async () => {
                              const next = !currentJob.published;
                              try {
                                await axios.post(`${API}/videos/${currentJob.id}/publish`, { published: next });
                                setCurrentJob((j) => ({ ...j, published: next }));
                                try {
                                  const lib = await axios.get(`${API}/videos`);
                                  setLibrary(lib.data);
                                } catch (_err) { /* noop */ }
                              } catch (_e) { /* noop */ }
                            }}
                            className={currentJob.published ? "btn-ghost text-sm" : "btn-primary text-sm"}
                            data-testid="btn-publish-toggle"
                          >
                            {currentJob.published ? (<><EyeOff className="w-4 h-4 inline mr-1.5" />Unpublish</>)
                                                  : (<><Eye className="w-4 h-4 inline mr-1.5" />Publish publicly</>)}
                          </button>
                        </div>
                        {currentJob.published && currentJob.slug && (
                          <div className="mt-3 flex items-center gap-2 text-xs text-[color:var(--muted)] break-all">
                            <LinkIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            <a href={`/s/${currentJob.slug}`} target="_blank" rel="noopener noreferrer"
                               className="text-white hover:text-[color:var(--accent)] transition-colors"
                               data-testid="public-url-link">
                              {typeof window !== "undefined" ? `${window.location.origin}/s/${currentJob.slug}` : `/s/${currentJob.slug}`}
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 mt-4 flex-wrap">
                        <a
                          href={mediaUrl(currentVideoUrl)}
                          download={`${currentJob.slug || currentJob.title || "narration"}-${previewAudio || currentJob.language}.mp4`}
                          className="btn-primary flex items-center gap-2 text-sm"
                          data-testid="btn-download"
                        ><Download className="w-4 h-4" /> Download MP4</a>
                        <button onClick={() => { setCurrentJob(null); setPreviewAudioLang(null); setPreviewSubLang(null); }} className="btn-ghost text-sm" data-testid="btn-new">New project</button>
                      </div>

                      {currentJob.published && currentJob.slug && (
                        <div className="mt-4">
                          <ShareButtons
                            url={typeof window !== "undefined" ? `${window.location.origin}/s/${currentJob.slug}` : ""}
                            crawlerUrl={typeof window !== "undefined" ? `${window.location.origin}/api/share/${currentJob.slug}` : ""}
                            title={currentJob.title}
                            text={`Watch "${currentJob.title}" in 14 languages`}
                          />
                        </div>
                      )}

                      <ImpactDetails job={currentJob} setJob={setCurrentJob} reloadLibrary={async () => {
                        try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) {}
                      }} />
                    </>
                  )}
                  {currentJob.status === "failed" && (
                    <button onClick={() => { setCurrentJob(null); setPreviewAudioLang(null); setPreviewSubLang(null); }} className="btn-ghost mt-4 text-sm">Try again</button>
                  )}
                </div>
              )}
            </div>

            <div className="panel p-6">
              <div className="label-eyebrow mb-3">How it works</div>
              <ol className="text-sm text-[color:var(--muted)] space-y-2 leading-relaxed list-decimal list-inside">
                {mode === "dub" ? (
                  <>
                    <li>Upload your finished animated video.</li>
                    <li>Upload the original narration audio — we transcribe it locally.</li>
                    <li>Edit the script if you like, pick a target language and voice.</li>
                    <li>We translate, narrate and replace the audio track on your video.</li>
                  </>
                ) : (
                  <>
                    <li>Upload 3–8 images in the order you want them to appear.</li>
                    <li>Paste your story script in any language.</li>
                    <li>Pick a target language and voice — translation is automatic.</li>
                    <li>We render a cinematic MP4 with Ken-Burns pans and narration.</li>
                  </>
                )}
              </ol>
            </div>
          </aside>
        </div>

        {/* Library */}
        {library.length > 0 && (
          <section className="mt-16">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-2">Recent renders</div>
                <h2 className="font-display text-3xl">Library</h2>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={openReorderModal}
                  className="text-xs uppercase tracking-[0.18em] text-[color:var(--muted)] hover:text-[color:var(--accent)] transition-colors flex items-center gap-1.5"
                  data-testid="library-reorder-btn"
                  title="Reorder how episodes play on Spotify/Apple — titles unchanged"
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  Reorder episodes
                </button>
                <button
                  onClick={renameAllBalkans}
                  className="text-xs uppercase tracking-[0.18em] text-[color:var(--muted)] hover:text-[color:var(--accent)] transition-colors flex items-center gap-1.5"
                  data-testid="library-rename-balkans-btn"
                  title="Normalise all Balkans-series episode titles to '01 - Balkans - …' format"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Rename Balkans series
                </button>
                <button
                  onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/trash`);
                      setTrash(r.data || []);
                      setShowTrash((s) => !s);
                    } catch (_e) { /* ignored */ }
                  }}
                  className="text-xs uppercase tracking-[0.18em] text-[color:var(--muted)] hover:text-white transition-colors flex items-center gap-1.5"
                  data-testid="library-trash-toggle"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {showTrash ? "Hide trash" : "Show trash"}
                </button>
              </div>
            </div>
            <StudioLibraryFilters library={library} onChange={setFilteredLibrary} />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="library-grid">
              {filteredLibrary.map((v) => (
                <div key={v.id} className="panel overflow-hidden group" data-testid={`library-item-${v.id}`}>
                <div className="relative aspect-video bg-black cursor-pointer"
                       onClick={() => {
                         if (v.status !== "done") return;
                         setCurrentJob(v);
                         // Library lives at the bottom of the page; the
                         // preview pane is at the top. Without scrolling
                         // the user can mistakenly think the click did
                         // nothing.
                         try {
                           document
                             .querySelector('[data-testid="preview-player"]')
                             ?.scrollIntoView({ behavior: "smooth", block: "center" });
                         } catch (_e) { /* ignored */ }
                       }}>
                    {v.thumbnail_url ? (
                      <img src={mediaUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[color:var(--muted)]">
                        {v.status === "failed" ? <X className="w-6 h-6 text-red-400" /> : <Loader2 className="w-6 h-6 animate-spin" />}
                      </div>
                    )}
                    {v.status === "done" && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                        <div className="w-12 h-12 rounded-full bg-[color:var(--accent)] flex items-center justify-center">
                          <Play className="w-5 h-5 text-black ml-0.5" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium truncate text-sm flex-1 min-w-0">{v.title}</div>
                      <button onClick={() => editTitle(v)}
                              className="text-[color:var(--muted)] hover:text-[color:var(--accent)] transition-colors flex-shrink-0"
                              title="Rename this story"
                              data-testid={`library-edit-title-${v.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteVideo(v.id)}
                              className="text-[color:var(--muted)] hover:text-red-400 transition-colors flex-shrink-0"
                              data-testid={`library-delete-${v.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-xs text-[color:var(--muted)] mt-1 flex items-center justify-between gap-2">
                      <span className="truncate">
                        {LANGUAGE_FLAG[v.language]} {v.language_name} · {v.voice?.split("-").slice(-1)[0]?.replace("Neural", "")}
                        {v.status !== "done" && <span className="ml-1 text-[color:var(--accent)]">· {v.status}</span>}
                        {v.tails_updating && (
                          <span className="ml-1 text-amber-300" data-testid={`tails-updating-${v.id}`}>
                            · updating clips{typeof v.progress === "number" && v.progress < 100 ? ` ${v.progress}%` : ""}…
                          </span>
                        )}
                      </span>
                      {v.status === "done" && (v.published ? (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--accent)] flex items-center gap-1 flex-shrink-0" title="Published publicly">
                          <Globe className="w-3 h-3" /> Live
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)] flex items-center gap-1 flex-shrink-0" title="Only visible to you">
                          <EyeOff className="w-3 h-3" /> Draft
                        </span>
                      ))}
                    </div>
                    <LanguageProgressStrip languages={v.languages} videoId={v.id} />
                    {/* Quick engagement signals — shown only for live stories
                        that have actually accumulated some response, so empty
                        new stories don't look unloved. */}
                    {v.status === "done" && v.published && (
                      v.view_count > 0 || v.helpful_count > 0 || v.not_helpful_count > 0
                    ) && (
                      <div
                        className="flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted)] mt-2"
                        data-testid={`library-stats-${v.id}`}
                      >
                        {v.view_count > 0 && (
                          <span className="inline-flex items-center gap-1" title="Views (rate-limited per IP)">
                            <Play className="w-3 h-3" /> {v.view_count.toLocaleString()}
                          </span>
                        )}
                        {v.helpful_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-[color:var(--accent)]" title="Readers who tapped Yes">
                            <ThumbsUp className="w-3 h-3" /> {v.helpful_count}
                          </span>
                        )}
                        {v.not_helpful_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-red-400/70" title="Readers who tapped Not for me">
                            <ThumbsDown className="w-3 h-3" /> {v.not_helpful_count}
                          </span>
                        )}
                      </div>
                    )}
                    {v.status === "done" && v.published && v.slug && (v.main_category || (v.labels && v.labels.length > 0) || v.country || v.year) && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-[color:var(--muted)]" data-testid={`library-taxonomy-summary-${v.id}`}>
                        {v.main_category_label && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[color:var(--accent)]/30 text-[color:var(--accent)]">
                            {v.main_category_label}
                          </span>
                        )}
                        {v.country && <span>📍 {v.country}</span>}
                        {v.year != null && <span>🗓 {v.year}</span>}
                        {v.story_length && <span>⏱ {v.story_length}</span>}
                        {(v.labels && v.labels.length > 0) && (
                          <StoryLabelBadges labels={v.labels} size="xs" />
                        )}
                      </div>
                    )}
                    {v.status === "done" && !v.published && (
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/videos/${v.id}/publish`, { published: true });
                            const lib = await axios.get(`${API}/videos`);
                            setLibrary(lib.data);
                          } catch (_e) { /* ignored */ }
                        }}
                        className="btn-primary text-sm py-2 w-full mt-3 flex items-center justify-center gap-1.5"
                        data-testid={`library-publish-${v.id}`}
                        title="Make this story publicly visible — unlocks the share tools and YouTube kit"
                      >
                        <Globe className="w-4 h-4" /> Publish publicly
                      </button>
                    )}
                    {v.status === "done" && v.published && v.slug && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                        <button
                          onClick={() => setSharePreview(v)}
                          className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`library-share-preview-${v.id}`}
                          title="See exactly what Facebook / WhatsApp / Twitter will show when this story is shared"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Share card
                        </button>
                        <button
                          onClick={() => setFbShare(v)}
                          className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-[#1877F2]/40 text-[#5099f5] hover:border-[#1877F2] hover:text-[#1877F2] transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`library-fb-post-${v.id}`}
                          title="Post this story's cover + caption directly to your Facebook Page — no copy/paste"
                        >
                          <Facebook className="w-3.5 h-3.5" />
                          Post to FB
                        </button>
                        <button
                          onClick={() => setFbPreview(v)}
                          className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[#5099f5] hover:text-[#5099f5] transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`library-fb-preview-${v.id}`}
                          title="See the exact image FB will show — and force FB to re-scrape if it's serving a stale preview"
                        >
                          <Facebook className="w-3.5 h-3.5" />
                          FB preview
                        </button>
                        <button
                          onClick={() => setYtKit(v)}
                          className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-[#FF0000]/40 text-[#ff5c5c] hover:border-[#FF0000] hover:text-[#FF0000] transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`library-youtube-kit-${v.id}`}
                          title="Download the MP4 + copy a ready-made title & description for YouTube or Spotify"
                        >
                          <Youtube className="w-3.5 h-3.5" />
                          YouTube kit
                        </button>
                        <button
                          onClick={() => setBodyEditor(v)}
                          className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`library-edit-body-${v.id}`}
                          title="Add long-form text + photos that appear below the video"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Article
                        </button>
                        <TranslateNowButton videoId={v.id} testidSuffix={v.id} />
                        <PreWarmDubsButton videoId={v.id} sourceLang={v.language} testidSuffix={v.id} />
                        <LanguageRefreshButton videoId={v.id} sourceLang={v.language} />
                        <TailSubsButton videoId={v.id} testidSuffix={v.id} />
                        <button
                          onClick={() => setTaxEditor(v)}
                          className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`library-taxonomy-${v.id}`}
                          title="Categorise this story — main category, country, year, tags, labels"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Categorise
                        </button>
                        <ThumbnailUploadButton
                          videoId={v.id}
                          title={v.title}
                          onUploaded={async () => {
                            try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
                          }}
                        />
                        <AddTailsButton
                          video={v}
                          uploadFileInChunks={uploadFileInChunks}
                          onQueued={async () => {
                            try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
                          }}
                        />
                        <SeriesLinkButton
                          video={v}
                          onSaved={async () => {
                            try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
                          }}
                        />
                        <MusicMixButton video={v} />
                        <RefreshEndcardButton
                          video={v}
                          onQueued={async () => {
                            try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
                          }}
                        />
                        <LanguagesButton
                          video={v}
                          languages={languages}
                          onSaved={async () => {
                            try { const lib = await axios.get(`${API}/videos`); setLibrary(lib.data); } catch (_e) { /* ignored */ }
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {library.length > 0 && filteredLibrary.length === 0 && (
              <div className="panel p-8 mt-6 text-center" data-testid="library-empty-filtered">
                <Filter className="w-8 h-8 mx-auto text-[color:var(--muted)] opacity-40" />
                <div className="mt-3 font-display text-lg">No stories match those filters</div>
                <div className="text-xs text-[color:var(--muted)] mt-1">
                  Try clearing the filters above or widening the category selection.
                </div>
              </div>
            )}

            {/* Reorder-episodes modal — lets the operator drag or
                use ↑/↓ arrows to rearrange how Spotify/Apple play
                through the series. Only `created_at` in Mongo is
                touched; titles/slugs stay identical. */}
            {showReorder && (
              <div
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                data-testid="reorder-modal"
                onClick={(e) => { if (e.target === e.currentTarget) setShowReorder(false); }}
              >
                <div className="panel max-w-2xl w-full max-h-[85vh] flex flex-col p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="label-eyebrow mb-1">Reorder episodes</div>
                      <div className="text-sm text-[color:var(--muted)]">
                        Top of the list = plays first on Spotify / Apple. Use ↑ / ↓ to move.
                      </div>
                    </div>
                    <button
                      onClick={() => setShowReorder(false)}
                      className="text-[color:var(--muted)] hover:text-white"
                      data-testid="reorder-cancel-btn"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                    {reorderRows.length === 0 && (
                      <div className="text-sm text-[color:var(--muted)] py-6 text-center">
                        No published stories yet.
                      </div>
                    )}
                    {reorderRows.map((row, idx) => (
                      <div
                        key={row.id}
                        className="flex items-center gap-3 p-2 rounded bg-white/[.04] hover:bg-white/[.08]"
                        data-testid={`reorder-row-${row.id}`}
                      >
                        <div className="w-6 text-xs text-[color:var(--muted)] font-mono">
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                        <div className="flex-1 truncate text-sm">{row.title}</div>
                        <button
                          onClick={() => moveReorderRow(idx, -1)}
                          disabled={idx === 0}
                          className="p-1 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`reorder-up-${row.id}`}
                          title="Move up"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveReorderRow(idx, 1)}
                          disabled={idx === reorderRows.length - 1}
                          className="p-1 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`reorder-down-${row.id}`}
                          title="Move down"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-white/10">
                    <button
                      onClick={() => setShowReorder(false)}
                      className="text-sm text-[color:var(--muted)] hover:text-white"
                      data-testid="reorder-close-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveReorder}
                      disabled={reorderRows.length === 0}
                      className="px-4 py-2 rounded bg-[color:var(--accent)] text-black text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="reorder-save-btn"
                    >
                      Save order
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Trash drawer — appears when the operator toggles "Show
                trash" above the Library grid. Soft-deleted stories
                stay here for 30 days before the hourly trash-sweep
                cron permanently purges them. One-click Restore lifts
                the `deleted` flag + republishes the doc in the main
                library instantly. */}
            {showTrash && (
              <div className="mt-10 panel p-6" data-testid="library-trash">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-eyebrow mb-1">Trash</div>
                    <div className="text-xs text-[color:var(--muted)]">
                      Soft-deleted stories. Auto-purged 30 days after deletion.
                    </div>
                  </div>
                  {trash.length > 0 && (
                    <div className="text-xs text-[color:var(--muted)]" data-testid="library-trash-count">
                      {trash.length} item{trash.length === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
                {trash.length === 0 ? (
                  <div className="text-sm text-[color:var(--muted)] py-6 text-center">
                    Nothing in trash. Deleted stories appear here for 30 days.
                  </div>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {trash.map((v) => {
                      const deletedAt = v.deleted_at ? new Date(v.deleted_at) : null;
                      const daysSince = deletedAt
                        ? Math.floor((Date.now() - deletedAt.getTime()) / 86400000)
                        : 0;
                      const daysLeft = Math.max(0, 30 - daysSince);
                      return (
                        <li
                          key={v.id}
                          className="py-3 flex items-center gap-3"
                          data-testid={`library-trash-item-${v.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{v.title || "Untitled"}</div>
                            <div className="text-[11px] text-[color:var(--muted)] mt-0.5">
                              {LANGUAGE_FLAG[v.language] || "🏳️"} {v.language_name || v.language}
                              {" · "}deleted {daysSince === 0 ? "today" : `${daysSince} day${daysSince === 1 ? "" : "s"} ago`}
                              {" · "}purges in {daysLeft} day{daysLeft === 1 ? "" : "s"}
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/videos/${v.id}/restore`);
                                const [lib, tr] = await Promise.all([
                                  axios.get(`${API}/videos`),
                                  axios.get(`${API}/trash`),
                                ]);
                                setLibrary(lib.data);
                                setTrash(tr.data || []);
                              } catch (_e) { /* tolerated */ }
                            }}
                            className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                            data-testid={`library-trash-restore-${v.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Restore
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Permanently delete "${v.title || "this story"}"? This can't be undone.`)) return;
                              try {
                                await axios.delete(`${API}/videos/${v.id}?hard=true`);
                                const tr = await axios.get(`${API}/trash`);
                                setTrash(tr.data || []);
                              } catch (_e) { /* tolerated */ }
                            }}
                            className="text-[11px] uppercase tracking-[0.18em] text-red-300/70 hover:text-red-200 transition-colors"
                            data-testid={`library-trash-purge-${v.id}`}
                          >
                            Purge now
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </section>
        )}

        <footer className="mt-20 py-10 text-center text-xs text-[color:var(--muted)] border-t border-white/5">
          Crafted with intention · Storylo Studio
        </footer>
      </main>

      {sharePreview && (
        <SharePreviewModal
          job={sharePreview}
          onClose={() => setSharePreview(null)}
          onSaved={async () => {
            // Re-fetch so the modal's `share_card` defaults stay in sync
            // (e.g. after a background-image upload).
            try {
              const lib = await axios.get(`${API}/videos`);
              setLibrary(lib.data);
              const fresh = lib.data.find((v) => v.id === sharePreview.id);
              if (fresh) setSharePreview(fresh);
            } catch (_e) { /* ignored */ }
          }}
        />
      )}

      {fbShare && (
        <FacebookShareModal
          video={fbShare}
          onClose={() => setFbShare(null)}
        />
      )}

      {fbPreview && (
        <FBPreviewModal
          video={fbPreview}
          onClose={() => setFbPreview(null)}
        />
      )}

      {ytKit && (
        <YouTubeKitModal
          video={ytKit}
          onClose={() => setYtKit(null)}
        />
      )}

      {bodyEditor && (
        <StoryBodyEditor
          video={bodyEditor}
          onClose={() => setBodyEditor(null)}
          onSaved={async () => {
            try {
              const lib = await axios.get(`${API}/videos`);
              setLibrary(lib.data);
            } catch (_e) { /* ignored */ }
          }}
        />
      )}

      {taxEditor && (
        <TaxonomyEditor
          video={taxEditor}
          onClose={() => setTaxEditor(null)}
          onSaved={async () => {
            try {
              const lib = await axios.get(`${API}/videos`);
              setLibrary(lib.data);
            } catch (_e) { /* ignored */ }
          }}
        />
      )}

      {wizardOpen && <StudioWizard onClose={() => setWizardOpen(false)} />}
    </div>
  );
}

export default App;

/* ----------------------------------------------------------------------- */
/* Share-card preview + edit modal — shows the auto-generated 1200×630 OG  */
/* card and lets the creator override the headline / eyebrow / tagline /   */
/* accent colour / emoji + upload a custom background. The preview re-     */
/* fetches with a debounce as you type, so you see the final card before   */
/* committing. Saves to PATCH /api/videos/{id}/share-card.                  */
/* ----------------------------------------------------------------------- */

const ACCENT_PRESETS = [
  { name: "Storylo amber", hex: "#EAB308" },
  { name: "Rescue red",    hex: "#EF4444" },
  { name: "Forest green",  hex: "#22C55E" },
  { name: "Ocean blue",    hex: "#3B82F6" },
  { name: "Royal purple",  hex: "#8B5CF6" },
  { name: "Hot pink",      hex: "#EC4899" },
];

// Curated set of emojis matched to common Storylo cause categories. Tap to
// drop into the share-card emoji field — saves creators the cross-platform
// keyboard-shortcut dance and nudges them toward tonally-appropriate picks
// (we deliberately exclude e.g. 😂 / 🔥 / 🎉 — wrong tone for trauma stories).
const EMOJI_QUICK_PICKS = [
  { e: "🐎", group: "Animal" },
  { e: "🐕", group: "Animal" },
  { e: "🐈", group: "Animal" },
  { e: "🐾", group: "Animal" },
  { e: "🎗️", group: "Awareness" },
  { e: "🎀", group: "Awareness" },
  { e: "💜", group: "Awareness" },
  { e: "💚", group: "Mental health" },
  { e: "🌱", group: "Mental health" },
  { e: "🤍", group: "Child welfare" },
  { e: "🌟", group: "Child welfare" },
  { e: "🌍", group: "Environment" },
  { e: "🌳", group: "Environment" },
  { e: "🌊", group: "Environment" },
  { e: "❤️", group: "Support" },
  { e: "✨", group: "Support" },
];

function TranslateNowButton({ videoId, testidSuffix }) {
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [info, setInfo] = useState("");

  const onClick = async () => {
    setState("running"); setInfo("");
    try {
      const { data } = await axios.post(
        `${API}/videos/${videoId}/pretranslate-all`,
        null,
        { timeout: 5 * 60 * 1000 }, // generous — translates 9 langs across script + body
      );
      const newScript = (data?.script?.translated || []).length;
      const newBody = (data?.body?.translated || []).length;
      const cachedScript = (data?.script?.already_cached || []).length;
      const cachedBody = (data?.body?.already_cached || []).length;
      const total = newScript + newBody;
      if (!data?.script?.had_script && !data?.body?.had_body) {
        setInfo("No script yet");
      } else if (total === 0 && (cachedScript || cachedBody)) {
        setInfo("All cached ✓");
      } else {
        setInfo(`Cached ${total}`);
      }
      setState("done");
      setTimeout(() => setState("idle"), 4000);
    } catch (e) {
      console.error("translate-all error:", e);
      setInfo(e?.response?.data?.detail || e.message || "Translate failed");
      setState("error");
      setTimeout(() => setState("idle"), 4000);
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={state === "running"}
      className={`text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border transition-colors flex items-center justify-center gap-1.5 ${
        state === "done" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
        : state === "error" ? "border-red-500/40 text-red-300 bg-red-500/10"
        : "border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
      }`}
      data-testid={`library-translate-${testidSuffix}`}
      title={info || "Pre-translate the script and article into all 14 languages so viewers worldwide get instant translations"}
    >
      {state === "running" ? (
        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Translating…</>
      ) : state === "done" ? (
        <><Check className="w-3.5 h-3.5" /> {info || "Cached"}</>
      ) : state === "error" ? (
        <><X className="w-3.5 h-3.5" /> Try again</>
      ) : (
        <><Globe className="w-3.5 h-3.5" /> Translate</>
      )}
    </button>
  );
}


function PreWarmDubsButton({ videoId, sourceLang, testidSuffix }) {
  // "Render every language's DUB video in the background" button.
  // Unlike TranslateNowButton (which only warms the text caches), this
  // kicks off the actual ffmpeg + TTS pipeline for each non-source
  // language so the very first viewer in every market gets instant
  // playback rather than a 60-120 s wait. Background work — returns
  // immediately with a count of queued / already-cached / already-
  // running languages.
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [info, setInfo] = useState("");

  const onClick = async () => {
    setState("running"); setInfo("");
    try {
      const { data } = await axios.post(
        `${API}/videos/${videoId}/prewarm-all-dubs`,
        null,
        { timeout: 60 * 1000 },
      );
      const queued = (data?.queued || []).length;
      const already = (data?.already_done || []).length;
      const running = (data?.already_running || []).length;
      if (queued === 0 && running === 0) {
        setInfo(already > 0 ? "All cached ✓" : "Nothing to do");
      } else if (queued === 0) {
        setInfo(`${running} rendering…`);
      } else {
        setInfo(`Queued ${queued}${already ? ` · ${already} cached` : ""}`);
      }
      setState("done");
      setTimeout(() => setState("idle"), 5000);
    } catch (e) {
      console.error("prewarm-dubs error:", e);
      setInfo(e?.response?.data?.detail || e.message || "Failed");
      setState("error");
      setTimeout(() => setState("idle"), 5000);
    }
  };

  const totalTargets = 14 - 1; // 14 supported langs minus source
  return (
    <button
      onClick={onClick}
      disabled={state === "running"}
      className={`text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border transition-colors flex items-center justify-center gap-1.5 ${
        state === "done" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
        : state === "error" ? "border-red-500/40 text-red-300 bg-red-500/10"
        : "border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
      }`}
      data-testid={`library-prewarm-dubs-${testidSuffix}`}
      title={info || `Pre-render this story's dub video in all ${totalTargets} other languages so every viewer worldwide gets instant playback`}
    >
      {state === "running" ? (
        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Queuing…</>
      ) : state === "done" ? (
        <><Check className="w-3.5 h-3.5" /> {info || "Queued"}</>
      ) : state === "error" ? (
        <><X className="w-3.5 h-3.5" /> Try again</>
      ) : (
        <><Volume2 className="w-3.5 h-3.5" /> Pre-warm dubs</>
      )}
    </button>
  );
}



function SharePreviewModal({ job, onClose, onSaved }) {
  const sc = job.share_card || {};
  const [headline, setHeadline] = useState(sc.headline || job.title || "");
  const [eyebrow, setEyebrow] = useState(sc.eyebrow || "STORYLO · A REAL STORY");
  const [tagline, setTagline] = useState(sc.tagline || job.impact_summary || "Stories that flow · 14 languages");
  const [accent, setAccent] = useState(sc.accent_color || "#EAB308");
  const [emoji, setEmoji] = useState(sc.emoji || "");
  const [applyEndcard, setApplyEndcard] = useState(sc.apply_to_endcard !== false);
  const [bgUrl, setBgUrl] = useState(sc.background_url || "");

  const [editing, setEditing] = useState(Boolean(sc.headline || sc.eyebrow || sc.tagline));
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [error, setError] = useState("");
  // The preview image refetches with this `version` query param. Bumped on
  // mount, on save, and after the debounced live-preview kicks in.
  const [previewVersion, setPreviewVersion] = useState(() => Date.now());
  const fileInputRef = useRef(null);

  const storyUrl =
    typeof window !== "undefined" ? `${window.location.origin}/api/share/${job.slug}` : "";
  const cardUrl = `${mediaUrl(`/api/og-image/${encodeURIComponent(job.slug)}.png`)}?v=${previewVersion}`;

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Debounced live preview: 700ms after the last keystroke we PATCH the
  // overrides and bump `previewVersion` so the <img> refetches.
  useEffect(() => {
    if (!editing) return; // read-only mode — no auto-save
    const t = setTimeout(async () => {
      setError("");
      setSaving(true);
      try {
        await axios.patch(`${API}/videos/${job.id}/share-card`, {
          headline, eyebrow, tagline,
          accent_color: accent,
          emoji,
          apply_to_endcard: applyEndcard,
        });
        setPreviewVersion(Date.now());
        setSavedAt(Date.now());
      } catch (e) {
        setError(e?.response?.data?.detail || "Couldn't save — try again.");
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headline, eyebrow, tagline, accent, emoji, applyEndcard, editing]);

  const onCopy = async () => {
    try {
      const bust = `v=${Date.now().toString(36)}`;
      await navigator.clipboard.writeText(
        storyUrl.includes("?") ? `${storyUrl}&${bust}` : `${storyUrl}?${bust}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) { /* ignored */ }
  };

  const onPickBackground = () => fileInputRef.current?.click();

  const onBackgroundFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setError("Background must be a JPG, PNG or WebP image.");
      return;
    }
    setError("");
    setUploadingBg(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(
        `${API}/videos/${job.id}/share-card/background`, form,
      );
      setBgUrl(res.data.background_url);
      setPreviewVersion(Date.now());
      setSavedAt(Date.now());
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Background upload failed.");
    } finally {
      setUploadingBg(false);
    }
  };

  const onResetBackground = async () => {
    setError("");
    try {
      await axios.patch(`${API}/videos/${job.id}/share-card`, {
        background_url: "",
      });
      setBgUrl("");
      setPreviewVersion(Date.now());
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't reset.");
    }
  };

  const onResetAll = async () => {
    if (!window.confirm("Reset all share-card customisations to the auto-generated defaults?")) return;
    setError("");
    setSaving(true);
    try {
      await axios.patch(`${API}/videos/${job.id}/share-card`, { __reset__: true });
      setHeadline(job.title || "");
      setEyebrow("STORYLO · A REAL STORY");
      setTagline(job.impact_summary || "Stories that flow · 14 languages");
      setAccent("#EAB308");
      setEmoji("");
      setBgUrl("");
      setApplyEndcard(true);
      setEditing(false);
      setPreviewVersion(Date.now());
      setSavedAt(Date.now());
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't reset.");
    } finally {
      setSaving(false);
    }
  };

  const fieldCls =
    "w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[color:var(--accent)] transition-colors";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
      onClick={onClose}
      data-testid="share-preview-modal"
    >
      <div
        className="panel max-w-5xl w-full mx-auto my-12 md:my-6 p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Share-card preview"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5">
              {editing ? "Customize share card" : "Share preview"}
            </div>
            <div className="font-display text-2xl leading-tight">{job.title}</div>
            <div className="text-xs text-[color:var(--muted)] mt-1">
              This is exactly what Facebook, WhatsApp, iMessage and Twitter will show when someone shares this story.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[color:var(--muted)] hover:text-white transition-colors flex-shrink-0"
            data-testid="share-preview-close"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          {/* Live preview */}
          <div>
            <div className="rounded-xl overflow-hidden border border-white/10 bg-black relative">
              <img
                key={previewVersion}
                src={cardUrl}
                alt={`Share card for ${job.title}`}
                className="w-full h-auto block"
                data-testid="share-preview-image"
              />
              {saving && (
                <div className="absolute top-2 right-2 bg-black/70 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] text-[color:var(--accent)] flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Updating
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2 p-3 rounded-lg border border-white/10 bg-black/30">
              <LinkIcon className="w-4 h-4 text-[color:var(--muted)] flex-shrink-0" />
              <code className="text-xs text-[color:var(--muted)] truncate flex-1" data-testid="share-preview-url">
                {storyUrl}
              </code>
              <button
                onClick={onCopy}
                className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5 flex-shrink-0"
                data-testid="share-preview-copy"
                title={copied ? "Copied" : "Copy link"}
              >
                {copied ? <Check className="w-3.5 h-3.5 text-[color:var(--accent)]" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="text-[11px] text-[color:var(--muted)] leading-relaxed mt-3">
              Tip: if Facebook still shows an old preview, paste the link into{" "}
              <a
                href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(storyUrl)}`}
                target="_blank" rel="noopener noreferrer"
                className="underline hover:text-[color:var(--accent)]"
              >
                Facebook&apos;s Sharing Debugger
              </a>{" "}
              and click &quot;Scrape Again&quot;.
            </div>
          </div>

          {/* Edit form */}
          <div className="space-y-4">
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                data-testid="share-preview-customize"
                className="w-full p-4 rounded-lg border border-dashed border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors text-sm flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Customize this card
              </button>
            ) : (
              <>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Headline</label>
                  <input
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    maxLength={160}
                    className={fieldCls}
                    data-testid="share-edit-headline"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Eyebrow</label>
                  <input
                    value={eyebrow}
                    onChange={(e) => setEyebrow(e.target.value)}
                    maxLength={80}
                    className={fieldCls}
                    placeholder="STORYLO · A REAL STORY"
                    data-testid="share-edit-eyebrow"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Tagline</label>
                  <textarea
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    maxLength={200}
                    rows={2}
                    className={`${fieldCls} resize-none`}
                    data-testid="share-edit-tagline"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Accent</label>
                  <div className="flex flex-wrap gap-2 mt-1.5" data-testid="share-edit-accent-row">
                    {ACCENT_PRESETS.map((p) => (
                      <button
                        key={p.hex}
                        onClick={() => setAccent(p.hex)}
                        title={p.name}
                        aria-label={p.name}
                        className={`w-7 h-7 rounded-full border-2 transition-transform ${accent.toLowerCase() === p.hex.toLowerCase() ? "scale-110 border-white" : "border-white/20 hover:border-white/60"}`}
                        style={{ background: p.hex }}
                        data-testid={`share-edit-accent-${p.hex.replace("#", "")}`}
                      />
                    ))}
                    <input
                      type="color"
                      value={accent}
                      onChange={(e) => setAccent(e.target.value)}
                      className="w-7 h-7 rounded-full border-2 border-white/20 hover:border-white/60 bg-transparent cursor-pointer"
                      title="Pick a custom colour"
                      aria-label="Custom accent colour"
                      data-testid="share-edit-accent-custom"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Emoji <span className="text-[color:var(--muted)] normal-case tracking-normal">(optional)</span></label>
                  <input
                    value={emoji}
                    onChange={(e) => setEmoji(e.target.value)}
                    maxLength={4}
                    placeholder="Tap a suggestion below or type your own"
                    className={fieldCls}
                    data-testid="share-edit-emoji"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="share-edit-emoji-picker">
                    {EMOJI_QUICK_PICKS.map((p) => (
                      <button
                        key={p.e}
                        type="button"
                        onClick={() => setEmoji(p.e)}
                        title={p.group}
                        aria-label={`Use ${p.e} (${p.group})`}
                        className={`w-9 h-9 rounded-md flex items-center justify-center text-lg leading-none border transition-colors ${emoji === p.e ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10" : "border-white/10 hover:border-white/40"}`}
                        data-testid={`share-edit-emoji-pick-${p.e}`}
                      >
                        {p.e}
                      </button>
                    ))}
                    {emoji && (
                      <button
                        type="button"
                        onClick={() => setEmoji("")}
                        title="Remove emoji"
                        className="w-9 h-9 rounded-md flex items-center justify-center text-xs uppercase tracking-wider border border-white/10 hover:border-red-400 hover:text-red-300 transition-colors"
                        data-testid="share-edit-emoji-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-[color:var(--muted)] mt-1.5 leading-relaxed">
                    For trauma-sensitive stories, less is more — one emoji is plenty, none is fine.
                  </p>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Background</label>
                  <div className="flex gap-2 mt-1.5">
                    <button
                      onClick={onPickBackground}
                      disabled={uploadingBg}
                      className="flex-1 text-xs px-3 py-2 rounded-md border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center justify-center gap-1.5"
                      data-testid="share-edit-bg-upload"
                    >
                      {uploadingBg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {bgUrl ? "Replace" : "Upload"} image
                    </button>
                    {bgUrl && (
                      <button
                        onClick={onResetBackground}
                        className="text-xs px-3 py-2 rounded-md border border-white/10 hover:border-red-400 hover:text-red-400 transition-colors"
                        data-testid="share-edit-bg-reset"
                        title="Use the video thumbnail again"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onBackgroundFile}
                    className="hidden"
                    data-testid="share-edit-bg-input"
                  />
                  <p className="text-[10px] text-[color:var(--muted)] mt-1.5 leading-relaxed">
                    Best at 1200×630 or larger. Will be blurred and dimmed in the card so legibility stays high.
                  </p>
                </div>
                <label className="flex items-start gap-2.5 p-3 rounded-lg border border-white/10 cursor-pointer hover:border-white/20 transition-colors">
                  <input
                    type="checkbox"
                    checked={applyEndcard}
                    onChange={(e) => setApplyEndcard(e.target.checked)}
                    className="mt-0.5 accent-[color:var(--accent)]"
                    data-testid="share-edit-endcard"
                  />
                  <span className="text-xs leading-relaxed">
                    <span className="font-medium">Use this on the MP4 end-card too.</span>
                    <span className="block text-[10px] text-[color:var(--muted)] mt-0.5">Future renders of this story will show the customised headline + colour at the end. Existing renders are unchanged unless you re-render.</span>
                  </span>
                </label>
                {error && (
                  <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10" data-testid="share-edit-error">{error}</div>
                )}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">
                    {saving ? "Saving…" : savedAt ? "Saved" : "Live preview"}
                  </span>
                  <button
                    onClick={onResetAll}
                    className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--muted)] hover:text-red-400 transition-colors"
                    data-testid="share-edit-reset-all"
                  >
                    Reset all
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* ----------------------------------------------------------------------- */
/* Impact metadata editor — only shown after a render is complete. Lets    */
/* you mark a story as charity / animal_rescue / etc., add a donation      */
/* link, organisation name, location, etc. Saves to /api/videos/:id/impact */
/* ----------------------------------------------------------------------- */

/** Unwrap Facebook click-tracker URLs (l.facebook.com/l.php?u=…) and strip
 *  noisy fbclid / lid / utm_id query params so the green "Donate Now" button
 *  on the public page links straight to the GoFundMe / charity site. Safe
 *  for any other URL (returns it unchanged). */
function cleanDonationUrl(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";
  try {
    let u = new URL(trimmed);
    // Facebook redirect: extract the real URL from ?u=…
    if (/^l\.facebook\.com$|^lm\.facebook\.com$/i.test(u.hostname) && u.pathname === "/l.php") {
      const real = u.searchParams.get("u");
      if (real) {
        try { u = new URL(real); } catch { /* fall through */ }
      }
    }
    // Strip the noisiest tracking params on the inner URL.
    ["fbclid", "lid", "utm_id", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "h", "_aem"].forEach((k) => u.searchParams.delete(k));
    return u.toString();
  } catch {
    return trimmed; // not a parseable URL — let the input's `type=url` flag invalid input
  }
}

function ImpactDetails({ job, setJob, reloadLibrary }) {
  const [open, setOpen] = useState(Boolean(job.category && job.category !== "general"));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    category: job.category || "general",
    emotional_tone: job.emotional_tone || "informational",
    organization_name: job.organization_name || "",
    donation_link: job.donation_link || "",
    location: job.location || "",
    impact_summary: job.impact_summary || "",
    cause_tags: (job.cause_tags || []).join(", "),
  });
  // Sync local form when a different job becomes active in the right panel.
  useEffect(() => {
    setForm({
      category: job.category || "general",
      emotional_tone: job.emotional_tone || "informational",
      organization_name: job.organization_name || "",
      donation_link: job.donation_link || "",
      location: job.location || "",
      impact_summary: job.impact_summary || "",
      cause_tags: (job.cause_tags || []).join(", "),
    });
    setOpen(Boolean(job.category && job.category !== "general"));
    setSaved(false); setErr("");
  }, [job.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    setSaving(true); setErr(""); setSaved(false);
    try {
      const tags = form.cause_tags.split(",").map((s) => s.trim()).filter(Boolean);
      const cleanedLink = cleanDonationUrl(form.donation_link);
      // Reflect the cleaned value back in the form so the user sees what
      // was actually stored (no more pasted FB-wrapped tracker URL noise).
      if (cleanedLink !== form.donation_link) {
        setForm((p) => ({ ...p, donation_link: cleanedLink }));
      }
      const payload = {
        category: form.category,
        emotional_tone: form.emotional_tone,
        organization_name: form.organization_name.trim(),
        donation_link: cleanedLink,
        location: form.location.trim(),
        impact_summary: form.impact_summary.trim(),
        cause_tags: tags,
      };
      const { data } = await axios.patch(`${API}/videos/${job.id}/impact`, payload);
      setJob((j) => ({ ...j, ...data.fields, cause_tags: tags }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /** Save just the donation link (used by the prominent always-visible row).
   *  Cleans the URL first so Facebook tracker wrappers turn into the real
   *  GoFundMe / charity link before they hit the database. */
  const onSaveDonationLink = async () => {
    setSaving(true); setErr(""); setSaved(false);
    try {
      const cleanedLink = cleanDonationUrl(form.donation_link);
      if (cleanedLink !== form.donation_link) {
        setForm((p) => ({ ...p, donation_link: cleanedLink }));
      }
      const { data } = await axios.patch(`${API}/videos/${job.id}/impact`, {
        donation_link: cleanedLink,
      });
      setJob((j) => ({ ...j, ...data.fields }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onVerify = async () => {
    const token = window.prompt("Admin token (verify this story?):", "storylo-admin");
    if (!token) return;
    try {
      const { data } = await axios.post(`${API}/videos/${job.id}/verify`,
        { verified: !job.verified },
        { headers: { "X-Admin-Token": token } });
      setJob((j) => ({ ...j, verified: data.verified }));
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || "Verify failed");
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/5" data-testid="impact-details">
      {/* Always-visible Donate Now field — was previously buried inside the
          collapsed Impact-details section, which made volunteers paste their
          GoFundMe link into the article body instead. Surfacing it here turns
          on the green "Donate Now" pill on the public story page. */}
      <div className="mb-4 rounded-xl p-3" style={{ background: "rgba(16, 185, 129, 0.07)", border: "1px solid rgba(16, 185, 129, 0.2)" }} data-testid="donate-link-quick">
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-3.5 h-3.5" style={{ color: "#10b981" }} />
          <span className="text-[11px] uppercase tracking-[0.22em]" style={{ color: "#10b981" }}>Donate Now link</span>
          {form.donation_link && (
            <span className="text-[10px] text-[color:var(--muted)] ml-auto flex items-center gap-1">
              <Check className="w-3 h-3" style={{ color: "#10b981" }} /> Live on the public page
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="url"
            className="input-base flex-1 px-3 py-2 text-sm"
            placeholder="https://www.gofundme.com/f/your-campaign"
            value={form.donation_link}
            onChange={(e) => setForm({ ...form, donation_link: e.target.value })}
            onBlur={() => {
              const c = cleanDonationUrl(form.donation_link);
              if (c !== form.donation_link) setForm((p) => ({ ...p, donation_link: c }));
            }}
            data-testid="donate-link-quick-input"
          />
          <button
            type="button"
            onClick={onSaveDonationLink}
            disabled={saving || (job.donation_link || "") === cleanDonationUrl(form.donation_link)}
            className="btn-primary text-sm whitespace-nowrap flex items-center gap-1.5"
            style={{ background: "#10b981", color: "#04150d" }}
            data-testid="donate-link-quick-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5" />}
            {saved ? "Saved ✓" : "Save"}
          </button>
        </div>
        <p className="text-[11px] text-[color:var(--muted)] mt-2 leading-relaxed">
          Paste a GoFundMe / JustGiving / charity URL. We auto-unwrap Facebook share-tracker links so the button goes straight to your campaign. No need to put this in the article body.
        </p>
      </div>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] hover:text-white transition-colors flex items-center gap-1.5"
        data-testid="impact-details-toggle"
      >
        {open ? "▾" : "▸"} Impact details {form.category !== "general" && <span className="text-[color:var(--accent)]">· {form.category.replace("_", " ")}</span>}
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Category</span>
              <select className="input-base w-full px-3 py-2 text-sm"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      data-testid="impact-category">
                <option value="general">General</option>
                <option value="animal_rescue">Animal Rescue</option>
                <option value="charity">Charity</option>
                <option value="social_impact">Social Impact</option>
                <option value="environment">Environment</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Tone</span>
              <select className="input-base w-full px-3 py-2 text-sm"
                      value={form.emotional_tone}
                      onChange={(e) => setForm({ ...form, emotional_tone: e.target.value })}
                      data-testid="impact-tone">
                <option value="informational">Informational</option>
                <option value="uplifting">Uplifting</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
          </div>
          <input className="input-base w-full px-3 py-2 text-sm"
                 placeholder="Organisation (e.g. Rescue Animals Tarifa)"
                 value={form.organization_name}
                 onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                 data-testid="impact-org" />
          <input type="url" className="input-base w-full px-3 py-2 text-sm"
                 placeholder="Donation link (https://…)"
                 value={form.donation_link}
                 onChange={(e) => setForm({ ...form, donation_link: e.target.value })}
                 onBlur={() => {
                   const c = cleanDonationUrl(form.donation_link);
                   if (c !== form.donation_link) setForm((p) => ({ ...p, donation_link: c }));
                 }}
                 data-testid="impact-donation-link" />
          <input className="input-base w-full px-3 py-2 text-sm"
                 placeholder="Location (e.g. Tarifa, Spain)"
                 value={form.location}
                 onChange={(e) => setForm({ ...form, location: e.target.value })}
                 data-testid="impact-location" />
          <input className="input-base w-full px-3 py-2 text-sm"
                 placeholder="Cause tags (comma-separated, e.g. horses, sanctuary, rehab)"
                 value={form.cause_tags}
                 onChange={(e) => setForm({ ...form, cause_tags: e.target.value })}
                 data-testid="impact-tags" />
          <textarea className="input-base w-full px-3 py-2 text-sm leading-relaxed resize-y"
                    placeholder="Short impact summary — one or two sentences shown on the story page."
                    rows={3}
                    value={form.impact_summary}
                    onChange={(e) => setForm({ ...form, impact_summary: e.target.value })}
                    data-testid="impact-summary" />
          {err && <p className="text-xs text-rose-400">{err}</p>}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onSave} disabled={saving}
                    className="btn-primary text-sm flex items-center gap-2"
                    data-testid="impact-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {saved ? "Saved ✓" : "Save details"}
            </button>
            {typeof window !== "undefined" && new URLSearchParams(window.location.search).get("admin") === "1" && (
              <button onClick={onVerify} className="btn-ghost text-sm" data-testid="impact-verify">
                {job.verified ? "Un-verify" : "Mark verified ✓"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
